import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  Skeleton,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Card,
  Switch,
  Alert,
  Stack,
  Divider,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  FormLabel,
  // CircularProgress,
  Autocomplete,
} from "@mui/material";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  LocalOffer as LocalOfferIcon,
  AccountBalance as AccountBalanceIcon,
  Inventory as InventoryIcon,
  Scale as ScaleIcon,
  Edit as EditIcon,
  QrCode,
  Person,
  Phone,
  Home,
  Payments,
  ShoppingBag,
  Calculate,
  Summarize,
} from "@mui/icons-material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DataContext } from "../../context/DataContext";
import { format } from "date-fns";
import QRScanner from "../../components/QRScanner";
import {
  generateBillHTML,
  getBillStyles,
  parseItemObject,
} from "../../components/billPrintUtils";
import WeightEditDialog from '../../components/WeightEditDialog';
import WeightAdjustmentDialog from '../../components/WeightAdjustmentDialog';


// Add these skeleton components at the top of your file (after imports)

// Skeleton Components
const CustomerInfoSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
      <Skeleton variant="rounded" width={36} height={36} />
      <Skeleton variant="text" width={200} height={32} />
    </Stack>
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
      <Skeleton variant="rounded" height={56} />
    </Stack>
  </Card>
);

const ItemsSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={2}
    >
      <Skeleton variant="text" width={150} height={32} />
      <Stack direction="row" spacing={2}>
        <Skeleton variant="rounded" width={180} height={40} />
        <Skeleton variant="rounded" width={120} height={40} />
      </Stack>
    </Stack>

    <Paper sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: "transparent" }}>
      <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} md={3} key={i}>
            <Skeleton variant="rounded" height={40} />
          </Grid>
        ))}
      </Grid>
    </Paper>

    <TableContainer
      component={Paper}
      sx={{ borderRadius: 2, bgcolor: "transparent" }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              "Item",
              "HUID",
              "Weight",
              "Rate",
              "Wastage",
              "Total",
              "Actions",
            ].map((col) => (
              <TableCell key={col}>
                <Skeleton variant="text" width={60} height={24} />
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {[1, 2, 3].map((row) => (
            <TableRow key={row}>
              {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                <TableCell key={cell}>
                  <Skeleton variant="text" width={80} height={32} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

const GstSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Skeleton variant="text" width={120} height={28} sx={{ mb: 1 }} />
    <Skeleton variant="rounded" width={200} height={42} sx={{ mb: 2 }} />
    <Skeleton variant="rounded" height={56} />
  </Card>
);

const PaymentSectionSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Skeleton variant="text" width={150} height={32} sx={{ mb: 2 }} />
    <Grid container spacing={2}>
      {[1, 2, 3, 4].map((i) => (
        <Grid item xs={12} md={6} key={i}>
          <Skeleton variant="rounded" height={56} />
        </Grid>
      ))}
    </Grid>
  </Card>
);

const SummarySkeleton = ({ isDark }) => (
  <Card
    sx={{ p: 0, borderRadius: 3, overflow: "hidden", bgcolor: "transparent" }}
  >
    <Box sx={{ p: 3, pb: 2 }}>
      <Skeleton variant="rounded" width={200} height={36} />
    </Box>
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width={150} height={28} sx={{ mb: 2 }} />
      {[1, 2, 3].map((i) => (
        <Box key={i} sx={{ mb: 2 }}>
          <Stack direction="row" justifyContent="space-between" mb={1}>
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={100} height={20} />
          </Stack>
          <Skeleton variant="rounded" height={4} />
        </Box>
      ))}

      <Box sx={{ p: 2, borderRadius: 1, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between">
          <Skeleton variant="text" width={100} height={28} />
          <Skeleton variant="text" width={120} height={32} />
        </Stack>
      </Box>

      <Skeleton variant="text" width={130} height={28} sx={{ my: 2 }} />
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {[1, 2].map((i) => (
          <Grid item xs={6} key={i}>
            <Skeleton variant="rounded" height={80} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={4} key={i}>
            <Skeleton variant="rounded" height={70} />
          </Grid>
        ))}
      </Grid>
    </Box>
  </Card>
);

const ActionButtonsSkeleton = ({ isDark }) => (
  <Card sx={{ p: 3, borderRadius: 3, bgcolor: "transparent" }}>
    <Stack direction="row" spacing={2}>
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" height={52} sx={{ flex: 1 }} />
    </Stack>
  </Card>
);

// Validation Schema
const validationSchema = Yup.object({
  customerName: Yup.string().required("Customer name is required"),
  handledBy: Yup.string().required("Seller name is required"),
  gstNumber: Yup.string(),
  panNumber: Yup.string(),
  transactionDate: Yup.date().required("Transaction date is required"),
  newCustomerContact: Yup.string().when(["isNewCustomer"], {
    is: true,
    then: (schema) =>
      schema.required("Contact number is required for new customers"),
    otherwise: (schema) => schema,
  }),
  newCustomerAddress: Yup.string(),
  newCustomerDob: Yup.date().nullable(),
  newCustomerAnniversary: Yup.date().nullable(),
  cashPayment: Yup.number().min(0, "Must be positive").default(0),
  onlinePayment: Yup.number().min(0, "Must be positive").default(0),
  chequePayment: Yup.number().min(0, "Must be positive").default(0),
  otherPayment: Yup.number().min(0, "Must be positive").default(0),
  paymentDone: Yup.number().min(0, "Must be positive").default(0),
  discount: Yup.number().min(0, "Must be positive").default(0),
  totalAmount: Yup.number().when(["transactionType"], {
    is: "total",
    then: (schema) =>
      schema
        .required("Total amount is required")
        .min(0.01, "Must be greater than 0"),
    otherwise: (schema) => schema.min(0, "Must be positive").default(0),
  }),
});

const ManualItemDialog = ({
  open,
  onClose,
  onSubmit,
  manualItemDetails,
  setManualItemDetails,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Get company details from context
  const { companyDetails } = useContext(DataContext);

  // State for master data
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  const handleChange = (field, value) => {
    const updated = { ...manualItemDetails, [field]: value };

    // Recalculate total when relevant fields change
    if (
      field === "weight" ||
      field === "metalRate" ||
      field === "wastage" ||
      field === "labour" ||
      field === "calculationType"
    ) {
      updated.total = calculateManualItemTotal(updated);
    }

    setManualItemDetails(updated);
  };

  const calculateManualItemTotal = (details) => {
    const weight = parseFloat(details.weight) || 0;
    const metalRate = parseFloat(details.metalRate) || 0;
    const wastage = parseFloat(details.wastage) || 0;
    const labour = parseFloat(details.labour) || 0;

    if (details.calculationType === "labour") {
      return (metalRate + labour) * weight;
    } else {
      // wastage calculation
      return weight * ((100 + wastage) / 100) * metalRate;
    }
  };

  const handleSubmit = () => {
    console.log("Submitting manual item:", manualItemDetails);
    if (manualItemDetails.metal === "Gold") {
      manualItemDetails.silverType = "gold";
    }
    onSubmit(manualItemDetails);
    setManualItemDetails({
      name: "",
      metal: "Silver",
      silverType: "999silver",
      calculationType: "labour",
      metalRate: "",
      labour: "",
      wastage: "",
      weight: "",
      hsnCode: "",
      huid: "",
      total: 0,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: 700,
          background: isDark
            ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
            : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
          backgroundClip: "text",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <AddIcon />
        Add from Gross Stock
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Item Name with Autocomplete */}
          <Autocomplete
            freeSolo
            options={itemCategories}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return `${option.name} (${option.shortcut})`;
            }}
            value={manualItemDetails.name}
            onChange={(event, newValue) => {
              if (typeof newValue === "string") {
                handleChange("name", newValue);
              } else if (newValue && newValue.name) {
                handleChange("name", newValue.name);
              }
            }}
            onInputChange={(event, newInputValue) => {
              handleChange("name", newInputValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Item Name *"
                fullWidth
                size="small"
                required
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2">{option.name}</Typography>
                  <Chip
                    label={option.shortcut}
                    size="small"
                    sx={{ ml: 1, fontSize: "0.7rem" }}
                  />
                </Box>
              </li>
            )}
          />

          {/* Metal Selection with Autocomplete */}
          <Autocomplete
            options={metalTypes}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return `${option.name} (${option.shortcut})`;
            }}
            value={
              metalTypes.find((m) => m.name === manualItemDetails.metal) || null
            }
            onChange={(event, newValue) => {
              if (newValue) {
                handleChange("metal", newValue.name);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Metal *"
                fullWidth
                size="small"
                required
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography variant="body2">{option.name}</Typography>
                  <Chip
                    label={option.shortcut}
                    size="small"
                    sx={{ ml: 1, fontSize: "0.7rem" }}
                  />
                </Box>
              </li>
            )}
          />

          {/* Silver Type (only if metal is Silver) */}
          {manualItemDetails.metal === "Silver" && (
            <FormControl size="small" fullWidth>
              <InputLabel>Silver Type *</InputLabel>
              <Select
                value={manualItemDetails.silverType}
                onChange={(e) => handleChange("silverType", e.target.value)}
                label="Silver Type"
              >
                <MenuItem value="999silver">999 Silver</MenuItem>
                <MenuItem value="925silver">925 Silver</MenuItem>
                <MenuItem value="mixsilver">Mix Silver</MenuItem>
                <MenuItem value="silver">Standard Silver</MenuItem>
                <MenuItem value="oldsilver">Old Silver</MenuItem>
              </Select>
            </FormControl>
          )}

          {/* Weight */}
          <TextField
            label="Weight (g) *"
            type="text"
            value={manualItemDetails.weight}
            onChange={(e) => handleChange("weight", e.target.value)}
            fullWidth
            size="small"
            required
            InputProps={{
              endAdornment: <InputAdornment position="end">g</InputAdornment>,
            }}
          />

          {/* Metal Rate */}
          <TextField
            label="Metal Rate (per gram) *"
            type="text"
            value={manualItemDetails.metalRate}
            onChange={(e) => handleChange("metalRate", e.target.value)}
            fullWidth
            size="small"
            required
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
            }}
          />

          {/* Calculation Type Selection */}
          <FormControl component="fieldset">
            <FormLabel component="legend">Calculation Type *</FormLabel>
            <RadioGroup
              row
              value={manualItemDetails.calculationType}
              onChange={(e) => handleChange("calculationType", e.target.value)}
            >
              <FormControlLabel
                value="wastage"
                control={<Radio size="small" />}
                label="Wastage %"
              />
              <FormControlLabel
                value="labour"
                control={<Radio size="small" />}
                label="Labour (per gram)"
              />
            </RadioGroup>
          </FormControl>

          {/* Wastage or Labour Input */}
          {manualItemDetails.calculationType === "wastage" ? (
            <TextField
              label="Wastage %"
              type="text"
              value={manualItemDetails.wastage}
              onChange={(e) => handleChange("wastage", e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
            />
          ) : (
            <TextField
              label="Labour (per gram)"
              type="text"
              value={manualItemDetails.labour}
              onChange={(e) => handleChange("labour", e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
            />
          )}

          {/* HSN Code */}
          <TextField
            label="HSN Code"
            value={manualItemDetails.hsnCode}
            onChange={(e) => handleChange("hsnCode", e.target.value)}
            fullWidth
            size="small"
          />

          {/* HUID */}
          <TextField
            label="HUID"
            value={manualItemDetails.huid}
            onChange={(e) => handleChange("huid", e.target.value)}
            fullWidth
            size="small"
          />

          {/* Total (readonly) */}
          <TextField
            label="Total Amount"
            value={manualItemDetails.total.toFixed(2)}
            InputProps={{ readOnly: true }}
            fullWidth
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !manualItemDetails.name ||
            manualItemDetails.weight <= 0 ||
            manualItemDetails.metalRate <= 0
          }
        >
          Add Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CustomerTransaction = ({ onBack }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const {
    customers,
    addCustomer,
    customerTransactions,
    stock,
    addCustomerTransactionAndUpdateStock,
    companyDetails,
  } = useContext(DataContext);

  // Use master data
  const [itemCategories, setItemCategories] = useState([]);
  const [metalTypes, setMetalTypes] = useState([]);

  // Load master data from company details
  useEffect(() => {
    if (companyDetails && companyDetails.length > 0) {
      const company = companyDetails[0];

      // Load item categories
      if (company.items && Array.isArray(company.items)) {
        setItemCategories(company.items);
      }

      // Load metal types
      if (company.metals && Array.isArray(company.metals)) {
        setMetalTypes(company.metals);
      }
    }
  }, [companyDetails]);

  // Helper function to get item shortcut
  // const getItemShortcut = useCallback((itemName) => {
  //   if (!itemName) return '';
  //   const item = itemCategories.find(cat =>
  //     cat.name.toLowerCase() === itemName.toLowerCase()
  //   );
  //   return item ? item.shortcut : itemName.substring(0, 3).toUpperCase();
  // }, [itemCategories]);

  // // Helper function to get metal shortcut
  // const getMetalShortcut = useCallback((metalName) => {
  //   if (!metalName) return '';
  //   const metal = metalTypes.find(m =>
  //     m.name.toLowerCase() === metalName.toLowerCase()
  //   );
  //   return metal ? metal.shortcut : metalName.substring(0, 2).toUpperCase();
  // }, [metalTypes]);

  // State variables
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [showNewCustomerFields, setShowNewCustomerFields] = useState(false);
  const [transactionType, setTransactionType] = useState("detailed");
  const [items, setItems] = useState([]);
  const [showGST, setShowGST] = useState(false);
  const [gstMode, setGstMode] = useState("add"); // "add" or "adjust"
  const [gstType, setGstType] = useState("cgst_sgst"); // "cgst_sgst" or "igst"
  const [enableAdvancePayment, setEnableAdvancePayment] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentModes, setPaymentModes] = useState({
    cash: false,
    online: false,
    oldGold: false,
    oldSilver: false,
    other: false,
  });
  const [customOther, setCustomOther] = useState("");
  const [advancePayments, setAdvancePayments] = useState({});
  const [otherOptions, setOtherOptions] = useState([]);
  const [manualItemName, setManualItemName] = useState("");
  const [manualItemMetal, setManualItemMetal] = useState("");
  const [manualSerial, setManualSerial] = useState("");
  const [scannerOpen, setScannerOpen] = useState(false);
  const [manualItemDialogOpen, setManualItemDialogOpen] = useState(false);
  const [isTransactionSaved, setIsTransactionSaved] = useState(false);
  const [manualItemDetails, setManualItemDetails] = useState({
    name: "",
    metal: "Silver",
    silverType: "999silver",
    calculationType: "labour",
    metalRate: "",
    labour: "",
    wastage: "",
    weight: "",
    hsnCode: "",
    huid: "",
    total: 0,
  });
  // const [weightAdjustmentDialog, setWeightAdjustmentDialog] = useState({
  //   open: false,
  //   item: null,
  //   originalWeight: 0,
  //   newWeight: 0,
  //   weightDifference: 0,
  //   isWeightIncreased: false
  // });
  // const [pendingWeightChange, setPendingWeightChange] = useState(null);
  const [weightAdjustments, setWeightAdjustments] = useState({}); // Track weight adjustments per item
  const [currentAdjustmentItem, setCurrentAdjustmentItem] = useState(null);
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [editingItemWeight, setEditingItemWeight] = useState(null);
  const weightEditDialogOpen = false;
  // const [tempWeight, setTempWeight] = useState('');
  const [pendingWeightAdjustments, setPendingWeightAdjustments] = useState(null);

  // Function to open weight edit dialog
  const openWeightEditDialog = useCallback((item) => {
    // Get the original stock weight from the item's itemObject
    const originalStockWeight = item.itemObject?.weight || item.weight;

    setEditingItemWeight({
      id: item.id,
      name: item.name,
      originalStockWeight: originalStockWeight,
      currentWeight: item.weight
    });
    // setWeightEditDialogOpen(true);
  }, []);

  // Function to handle weight change confirmation
  const handleWeightChangeConfirm = useCallback((newWeight) => {
    if (!editingItemWeight) return;

    const originalStockWeight = editingItemWeight.originalStockWeight;
    // const weightDifference = newWeight - originalStockWeight;

    // Update the weight in the UI
    setItems(prev => prev.map(item => {
      if (item.id === editingItemWeight.id) {
        return { ...item, weight: newWeight };
      }
      return item;
    }));

    // Find the full item object for adjustment dialog
    const fullItem = items.find(item => item.id === editingItemWeight.id);

    // Track that this item needs adjustment
    setPendingWeightAdjustments(prev => ({
      ...prev,
      [editingItemWeight.id]: {
        originalWeight: originalStockWeight,
        newWeight: newWeight,
        item: fullItem,
        needsAdjustment: true
      }
    }));

    // Show the adjustment dialog
    setCurrentAdjustmentItem({
      ...fullItem,
      originalWeight: originalStockWeight,
      newWeight: newWeight
    });
    setAdjustmentDialogOpen(true);
    setEditingItemWeight(null);
  }, [editingItemWeight, items]);

  const updateItemField = useCallback(
    (id, field, value) => {
      // Weight is now handled by the dialog, so we don't process it here
      if (field === 'weight') {
        return; // Weight changes only through dialog
      }

      // Handle other fields normally
      setItems((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            const updatedItem = { ...item, [field]: value };

            // Calculate totals if needed
            if (field === 'metalRate' || field === 'wastage' || field === 'labour' || field === 'total') {
              const weight = parseFloat(updatedItem.weight) || 0;
              const metalRate = parseFloat(updatedItem.metalRate) || 0;
              const wastage = parseFloat(updatedItem.wastage) || 0;
              const labour = parseFloat(updatedItem.labour) || 0;
              const calculationType = updatedItem.calculationType || 'wastage';

              if (field === 'total') {
                if (weight > 0 && metalRate > 0 && updatedItem.adjustWastage !== false) {
                  if (calculationType === 'wastage') {
                    const calculatedWastage = ((updatedItem.total / (metalRate * weight)) - 1) * 100;
                    updatedItem.wastage = calculatedWastage > 0 ? parseFloat(calculatedWastage.toFixed(2)) : 0;
                  } else {
                    const calculatedLabour = (updatedItem.total / weight) - metalRate;
                    updatedItem.labour = calculatedLabour > 0 ? parseFloat(calculatedLabour.toFixed(2)) : 0;
                  }
                }
              } else {
                let currentAmount = 0;
                if (calculationType === 'wastage') {
                  currentAmount = metalRate * weight * ((wastage + 100) / 100);
                } else {
                  currentAmount = (metalRate + labour) * weight;
                }

                if (showGST && gstMode === "adjust" && updatedItem.adjustWastage !== false) {
                  if (calculationType === 'wastage') {
                    const denominator = 1.03 * metalRate * weight;
                    if (denominator > 0 && currentAmount > 0) {
                      const adjustedWastage = ((currentAmount / denominator) - 1) * 100;
                      const newAmount = metalRate * weight * ((adjustedWastage + 100) / 100);
                      updatedItem.total = parseFloat(newAmount.toFixed(2));
                      updatedItem.wastage = parseFloat(adjustedWastage.toFixed(2));
                    } else {
                      updatedItem.total = parseFloat(currentAmount.toFixed(2));
                    }
                  } else {
                    if (weight > 0 && currentAmount > 0) {
                      const adjustedLabourPerGram = (currentAmount / (1.03 * weight)) - metalRate;
                      const newAmount = (metalRate + adjustedLabourPerGram) * weight;
                      updatedItem.total = parseFloat(newAmount.toFixed(2));
                      updatedItem.labour = parseFloat(adjustedLabourPerGram.toFixed(2));
                    } else {
                      updatedItem.total = parseFloat(currentAmount.toFixed(2));
                    }
                  }
                } else {
                  updatedItem.total = parseFloat(currentAmount.toFixed(2));
                }
              }
            }

            return updatedItem;
          }
          return item;
        })
      );
    },
    [showGST, gstMode]
  );

  // Add this function to handle manual weight adjustment
  const handleManualWeightAdjustment = useCallback((item) => {
    setCurrentAdjustmentItem(item);
    setAdjustmentDialogOpen(true);
  }, []);

  // Add this function to handle adjustment completion
  const handleAdjustmentComplete = useCallback((result) => {
    if (!currentAdjustmentItem) return;

    // Update the item with adjustment info
    setItems(prev => prev.map(item => {
      if (item.id === currentAdjustmentItem.id) {
        return {
          ...item,
          weightAdjusted: true,
          weightAdjustmentData: {
            ...item.weightAdjustmentData,
            ...result,
            adjustedAt: new Date().toISOString()
          },
          adjustmentApplied: true
        };
      }
      return item;
    }));

    // Store adjustment in state for later processing
    setWeightAdjustments(prev => ({
      ...prev,
      [currentAdjustmentItem.id]: {
        item: currentAdjustmentItem,
        adjustment: result
      }
    }));

    setCurrentAdjustmentItem(null);
    alert('Weight adjustment applied successfully!');
  }, [currentAdjustmentItem]);

  // Add this function to handle skip adjustment
  const handleSkipAdjustment = useCallback(() => {
    if (!currentAdjustmentItem) return;

    setItems(prev => prev.map(item => {
      if (item.id === currentAdjustmentItem.id) {
        return {
          ...item,
          weightAdjusted: false,
          adjustmentSkipped: true
        };
      }
      return item;
    }));

    setCurrentAdjustmentItem(null);
    alert('Weight adjustment skipped. Item will be saved with new weight without stock adjustment.');
  }, [currentAdjustmentItem]);

  // Add this function to handle weight changes
  // const handleWeightChangeWithAdjustment = useCallback(async (itemId, newWeight) => {
  //   const item = items.find(i => i.id === itemId);
  //   if (!item) return;

  //   const originalWeight = parseFloat(item.weight) || 0;
  //   const weightDifference = newWeight - originalWeight;
  //   const isWeightIncreased = weightDifference > 0;

  //   // If no weight change, just update
  //   if (Math.abs(weightDifference) < 0.001) {
  //     updateItemField(itemId, 'weight', newWeight);
  //     return;
  //   }

  //   // Store the pending change and show dialog
  //   setPendingWeightChange({ itemId, newWeight, originalWeight, weightDifference, isWeightIncreased });
  //   // setWeightAdjustmentDialog({
  //   //   open: true,
  //   //   item,
  //   //   originalWeight,
  //   //   newWeight,
  //   //   weightDifference: Math.abs(weightDifference),
  //   //   isWeightIncreased
  //   // });
  // }, [items, updateItemField]);

  // // Add this function to handle adjustment completion
  // const handleWeightAdjustmentComplete = useCallback(async (result) => {
  //   if (!pendingWeightChange) return;

  //   const { itemId, newWeight, originalWeight } = pendingWeightChange;

  //   // Update the item weight in the UI
  //   updateItemField(itemId, 'weight', newWeight);

  //   // Store adjustment info in the item for later processing when saving
  //   setItems(prev => prev.map(item => {
  //     if (item.id === itemId) {
  //       return {
  //         ...item,
  //         weightAdjusted: true,
  //         weightAdjustmentData: {
  //           originalWeight,
  //           newWeight,
  //           weightDifference: Math.abs(originalWeight - newWeight),
  //           isWeightIncreased: originalWeight < newWeight,
  //           adjustmentAction: result.action,
  //           adjustmentDetails: result
  //         }
  //       };
  //     }
  //     return item;
  //   }));

  //   // Clear pending change
  //   setPendingWeightChange(null);
  // }, [pendingWeightChange, updateItemField]);


  // Formik initialization
  const formik = useFormik({
    initialValues: {
      customerName: "",
      handledBy: "",
      gstNumber: "",
      panNumber: "",
      transactionDate: new Date(),
      newCustomerContact: "",
      newCustomerAddress: "",
      newCustomerDob: null,
      newCustomerAnniversary: null,
      cashPayment: "",
      onlinePayment: "",
      chequePayment: "",
      otherPayment: "",
      paymentDone: 0,
      discount: "",
      totalAmount: 0,
      isNewCustomer: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });

  // Helper functions
  const getLastCustomerTransaction = useCallback(() => {
    if (!customerTransactions || customerTransactions.length === 0) {
      return { gst: null, nonGst: null };
    }

    let lastGst = null;
    let lastNonGst = null;

    for (let i = customerTransactions.length - 1; i >= 0; i--) {
      const transaction = customerTransactions[i];
      if (transaction.transactionDetails) {
        const gstFlag = transaction.transactionDetails.gstApplicable;
        if (gstFlag === true && !lastGst) lastGst = transaction;
        if (gstFlag === false && !lastNonGst) lastNonGst = transaction;
      }
      if (lastGst && lastNonGst) break;
    }

    return { gst: lastGst, nonGst: lastNonGst };
  }, [customerTransactions]);

  const calculateItemTotal = useCallback(
    (
      weight,
      metalRate,
      wastage,
      labour,
      calculationType,
      adjustForGST = false,
      targetAmount = null
    ) => {
      const weightNum = parseFloat(weight) || 0;
      const metalRateNum = parseFloat(metalRate) || 0;
      const wastageNum = parseFloat(wastage) || 0;
      const labourNum = parseFloat(labour) || 0;

      let total = 0;

      if (calculationType === "labour") {
        total = (metalRateNum + labourNum) * weightNum;
      } else {
        // wastage calculation
        total = metalRateNum * weightNum * ((wastageNum + 100) / 100);
      }

      // If we need to adjust for GST to keep amount same
      if (adjustForGST && targetAmount && metalRateNum > 0 && weightNum > 0) {
        const denominator = 1.03 * metalRateNum * weightNum;
        if (denominator > 0) {
          const adjustedWastage = (targetAmount / denominator - 1) * 100;
          const newTotal =
            metalRateNum * weightNum * ((adjustedWastage + 100) / 100);
          return {
            total: newTotal,
            adjustedWastage: parseFloat(adjustedWastage.toFixed(2)),
            adjustedLabour: parseFloat(
              ((adjustedWastage * metalRateNum) / 100).toFixed(2)
            ),
          };
        }
      }

      return total;
    },
    []
  );

  // Memoized calculations
  const calculations = useMemo(() => {
    let subtotal = 0;

    if (transactionType === "detailed") {
      subtotal = items.reduce(
        (sum, item) => sum + (parseFloat(item.total) || 0),
        0
      );
    } else {
      subtotal = parseFloat(formik.values.totalAmount) || 0;
    }

    let sub = subtotal;
    let discountAmount = parseFloat(formik.values.discount) || 0;
    let cgst = 0,
      sgst = 0,
      igst = 0,
      grandTotal = subtotal;

    if (showGST) {
      if (gstMode === "add") {
        // Add GST on top
        if (gstType === "igst") {
          igst = subtotal * 0.03; // 3% IGST
          grandTotal = subtotal + igst - discountAmount;
        } else {
          cgst = subtotal * 0.015; // 1.5% CGST
          sgst = subtotal * 0.015; // 1.5% SGST
          grandTotal = subtotal + cgst + sgst - discountAmount;
        }
      } else {
        // Adjust mode - GST is included in the amount
        if (gstType === "igst") {
          igst = subtotal * 0.03;
          grandTotal = subtotal + igst - discountAmount;
        } else {
          cgst = subtotal * 0.015;
          sgst = subtotal * 0.015;
          grandTotal = subtotal + cgst + sgst - discountAmount;
        }
      }
    } else {
      grandTotal = subtotal - discountAmount;
    }

    return { subtotal: sub, cgst, sgst, igst, grandTotal };
  }, [
    items,
    formik.values.totalAmount,
    formik.values.discount,
    showGST,
    gstMode,
    gstType,
    transactionType,
  ]);

  const advanceTotal = useMemo(
    () =>
      Object.values(advancePayments).reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0
      ),
    [advancePayments]
  );

  const totalPayments = useMemo(() => {
    return (
      (parseFloat(formik.values.cashPayment) || 0) +
      (parseFloat(formik.values.onlinePayment) || 0) +
      (parseFloat(formik.values.chequePayment) || 0) +
      (parseFloat(formik.values.otherPayment) || 0) +
      advanceTotal
    );
  }, [
    formik.values.cashPayment,
    formik.values.onlinePayment,
    formik.values.chequePayment,
    formik.values.otherPayment,
    advanceTotal,
  ]);

  const pendingAmount = useMemo(
    () => calculations.grandTotal - totalPayments,
    [calculations.grandTotal, totalPayments]
  );

  const { setFieldValue } = formik;

  // Update paymentDone when totalPayments changes
  useEffect(() => {
    setFieldValue("paymentDone", totalPayments);
  }, [totalPayments, setFieldValue]);

  // Recalculate items when GST mode changes
  useEffect(() => {
    if (items.length === 0) return;

    if (showGST && gstMode === "adjust") {
      // Switch to adjust mode - adjust based on CURRENT wastage values
      const updatedItems = items.map((item) => {
        if (
          item.currentWastage !== undefined &&
          item.weight &&
          item.metalRate
        ) {
          const currentAmount =
            item.currentTotal ||
            parseFloat(item.weight) *
            parseFloat(item.metalRate) *
            ((parseFloat(item.currentWastage) + 100) / 100);

          const calculation = calculateItemTotal(
            parseFloat(item.weight) || 0,
            parseFloat(item.metalRate) || 0,
            parseFloat(item.currentWastage) || 0,
            0,
            "wastage",
            true,
            currentAmount
          );

          if (typeof calculation === "object") {
            return {
              ...item,
              total: calculation.total,
              wastage: calculation.adjustedWastage,
              gstAdjusted: true,
            };
          }
        }
        return item;
      });

      setItems(updatedItems);
    } else if (showGST && gstMode === "add") {
      // Switch to add mode - restore CURRENT wastage values
      const updatedItems = items.map((item) => {
        if (item.currentWastage !== undefined) {
          return {
            ...item,
            wastage: item.currentWastage,
            total: item.currentTotal,
            gstAdjusted: false,
          };
        }
        return item;
      });

      setItems(updatedItems);
    }
  }, [showGST, gstMode, calculateItemTotal, items]);

  // Event handlers
  const handleQRScan = (qrData) => {
    const scannedQR = qrData.trim();
    const scannedItem = stock?.find((item) => item.qr === scannedQR);

    if (!scannedItem) {
      alert(`Item with QR code "${scannedQR}" not found in inventory`);
      return;
    }

    if (scannedItem?.sold === "Yes") {
      alert("This item has already been sold");
      return;
    }

    const alreadyAdded = items.some(
      (item) => item.itemObject && item.itemObject.qr === scannedQR
    );

    if (alreadyAdded) {
      alert("Item already added to transaction");
      return;
    }

    const weight = parseFloat(scannedItem.weight) || 0;
    const metalRate = parseFloat(scannedItem.metalRate) || 0;
    const makingCharges = parseFloat(scannedItem.makingCharges) || 0;

    // Calculate current amount with current making charges
    const currentAmount = weight * metalRate * ((makingCharges + 100) / 100);

    let totalAmount = currentAmount;
    let adjustedMakingCharges = makingCharges;

    // If GST is enabled and mode is "adjust", adjust the making charges and total
    if (showGST && gstMode === "adjust") {
      const calculation = calculateItemTotal(
        weight,
        metalRate,
        makingCharges,
        0,
        "wastage",
        true,
        currentAmount
      );

      if (typeof calculation === "object") {
        totalAmount = calculation.total;
        adjustedMakingCharges = calculation.adjustedWastage;
      }
    }

    const newItem = {
      id: Date.now(),
      name: scannedItem.name || "",
      huid: scannedItem.huid || "",
      weight: weight,
      metalRate: metalRate,
      wastage: adjustedMakingCharges,
      total: totalAmount,
      currentWastage: makingCharges,
      currentTotal: currentAmount,
      gstAdjusted: showGST && gstMode === "adjust",
      calculationType: "wastage",
      adjustWastage: true,
      itemObject: scannedItem,
    };

    setItems((prev) => [...prev, newItem]);
    setScannerOpen(false);

    let message = `Item "${scannedItem.name}" (QR: ${scannedQR}) added successfully!`;
    if (newItem.gstAdjusted) {
      message += `\nMaking charges adjusted from ${makingCharges}% to ${adjustedMakingCharges.toFixed(
        2
      )}% to include GST`;
    }
    alert(message);
  };

  const filteredCustomerSuggestions = useMemo(() => {
    if (!customerQuery?.trim()) return [];
    const query = customerQuery.toLowerCase().trim();
    return customers.filter(
      (c) =>
        c.name?.toLowerCase().includes(query) ||
        c.contact?.toString().toLowerCase().includes(query)
    );
  }, [customerQuery, customers]);

  useEffect(() => {
    if (filteredCustomerSuggestions.length > 0) {
      setCustomerSuggestions(filteredCustomerSuggestions);
      setIsNewCustomer(false);
      setShowNewCustomerFields(false);
    } else if (customerQuery?.trim()) {
      setIsNewCustomer(true);
      setCustomerSuggestions([{
        id: "new",
        name: `${customerQuery}`,
        isNew: true,
      }]);
    } else {
      setCustomerSuggestions([]);
      setIsNewCustomer(false);
      setShowNewCustomerFields(false);
    }
    setIsLoading(false)
  }, [filteredCustomerSuggestions, customerQuery]);

  const handleCustomerSelect = useCallback(
    (customer) => {
      if (customer.isNew) {
        formik.setFieldValue("customerName", customerQuery);
        formik.setFieldValue("isNewCustomer", true);
        setShowNewCustomerFields(true);
        setIsNewCustomer(true);
        setSelectedCustomer(null);
        // Clear GST and PAN when adding new customer
        formik.setFieldValue("gstNumber", "");
        formik.setFieldValue("panNumber", "");
      } else {
        setSelectedCustomer(customer);
        formik.setFieldValue("customerName", customer.name);
        // Set GST and PAN from selected customer
        formik.setFieldValue("gstNumber", customer.gstNo || "");
        formik.setFieldValue("panNumber", customer.panNo || "");
        setIsNewCustomer(false);
        setShowNewCustomerFields(false);
      }
      setCustomerQuery(customer.name);
      setCustomerSuggestions([]);
    },
    [customerQuery, formik]
  );

  const handleAddBySerial = useCallback(() => {
    if (!manualItemName) {
      alert("Please select an item type");
      return;
    }

    if (!manualSerial) {
      alert("Please enter serial number");
      return;
    }

    // Build filter conditions
    const stockItem = stock?.find((item) => {
      // Check serial number
      const serialMatch =
        item.serialNo?.toString() === manualSerial?.toString();

      // Check item name
      const nameMatch = item.name?.toString() === manualItemName?.toString();

      // Check metal (if selected)
      const metalMatch = manualItemMetal
        ? item.metal?.toString() === manualItemMetal?.toString()
        : true;

      // Check if not sold
      const notSold = item.sold === "No";

      return serialMatch && nameMatch && metalMatch && notSold;
    });

    if (stockItem?.sold === "Yes") {
      alert("This item has already been sold.");
      return;
    }

    if (stockItem) {
      const weight = parseFloat(stockItem.weight) || 0;
      const metalRate = parseFloat(stockItem.metalRate) || 0;
      const makingCharges = parseFloat(stockItem.makingCharges) || 0;

      const currentAmount = weight * metalRate * ((makingCharges + 100) / 100);

      let totalAmount = currentAmount;
      let adjustedMakingCharges = makingCharges;

      if (showGST && gstMode === "adjust") {
        const calculation = calculateItemTotal(
          weight,
          metalRate,
          makingCharges,
          0,
          "wastage",
          true,
          currentAmount
        );

        if (typeof calculation === "object") {
          totalAmount = calculation.total;
          adjustedMakingCharges = calculation.adjustedWastage;
        }
      }

      const newItem = {
        id: Date.now(),
        name: `${stockItem.name} ${stockItem?.metal || ""}`,
        huid: stockItem.huid || "",
        weight: weight,
        metalRate: metalRate,
        wastage: adjustedMakingCharges,
        total: totalAmount,
        currentWastage: makingCharges,
        currentTotal: currentAmount,
        gstAdjusted: showGST && gstMode === "adjust",
        calculationType: "wastage",
        adjustWastage: true,
        itemObject: stockItem,
      };

      setItems((prev) => [...prev, newItem]);

      // Reset the form fields
      setManualItemName("");
      setManualItemMetal("");
      setManualSerial("");

      let message = `Item "${stockItem.name}" added successfully!`;
      if (newItem.gstAdjusted) {
        message += `\nMaking charges adjusted from ${makingCharges}% to ${adjustedMakingCharges.toFixed(
          2
        )}% to include GST`;
      }
      alert(message);
    } else {
      // Enhanced error message with all criteria
      const metalFilter = manualItemMetal ? `, Metal: ${manualItemMetal}` : "";
      alert(
        `Item not found in stock. Serial: ${manualSerial}, Name: ${manualItemName}${metalFilter}`
      );
    }
  }, [
    manualItemName,
    manualItemMetal,
    manualSerial,
    stock,
    calculateItemTotal,
    showGST,
    gstMode,
  ]);

  const removeItemRow = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handlePaymentModeChange = useCallback(
    (mode) => {
      const newPaymentModes = { ...paymentModes, [mode]: !paymentModes[mode] };
      setPaymentModes(newPaymentModes);

      if (newPaymentModes[mode]) {
        setAdvancePayments((prev) => ({ ...prev, [mode]: "" }));
      } else {
        setAdvancePayments((prev) => {
          const newPayments = { ...prev };
          delete newPayments[mode];
          return newPayments;
        });
      }
    },
    [paymentModes]
  );

  const handleAdvancePaymentChange = useCallback((mode, value) => {
    setAdvancePayments((prev) => ({ ...prev, [mode]: value }));
  }, []);

  const handleAddCustomOther = useCallback(() => {
    if (!customOther.trim()) {
      alert("Please enter a custom payment type");
      return;
    }

    const optionName = customOther.trim();
    if (!otherOptions.includes(optionName)) {
      setOtherOptions((prev) => [...prev, optionName]);
      setAdvancePayments((prev) => ({ ...prev, [optionName]: "" }));
      setCustomOther("");
    } else {
      alert("This payment type already exists");
    }
  }, [customOther, otherOptions]);

  const handleCustomOtherPaymentChange = useCallback((optionName, value) => {
    setAdvancePayments((prev) => ({ ...prev, [optionName]: value }));
  }, []);

  const handleRemoveCustomOther = useCallback((optionName) => {
    setOtherOptions((prev) => prev.filter((opt) => opt !== optionName));
    setAdvancePayments((prev) => {
      const newPayments = { ...prev };
      delete newPayments[optionName];
      return newPayments;
    });
  }, []);

  const handleAddManualItem = async (itemDetails) => {
    let totalAmount = itemDetails.total;
    let wastageValue =
      itemDetails.calculationType === "wastage"
        ? parseFloat(itemDetails.wastage) || 0
        : 0;
    let labourValue =
      itemDetails.calculationType === "labour"
        ? parseFloat(itemDetails.labour) || 0
        : 0;
    let adjustedWastage = wastageValue;
    let adjustedLabour = labourValue;

    const currentAmount =
      itemDetails.calculationType === "wastage"
        ? parseFloat(itemDetails.weight) *
        parseFloat(itemDetails.metalRate) *
        ((wastageValue + 100) / 100)
        : (parseFloat(itemDetails.metalRate) + labourValue) *
        parseFloat(itemDetails.weight);

    if (showGST && gstMode === "adjust") {
      if (itemDetails.calculationType === "wastage") {
        const calculation = calculateItemTotal(
          itemDetails.weight,
          itemDetails.metalRate,
          wastageValue,
          0,
          "wastage",
          true,
          currentAmount
        );

        if (typeof calculation === "object") {
          totalAmount = calculation.total;
          adjustedWastage = calculation.adjustedWastage;
        }
      } else if (itemDetails.calculationType === "labour") {
        const weightNum = parseFloat(itemDetails.weight) || 0;
        const metalRateNum = parseFloat(itemDetails.metalRate) || 0;
        if (weightNum > 0) {
          const adjustedLabourPerGram =
            currentAmount / (1.03 * weightNum) - metalRateNum;
          adjustedLabour = parseFloat(adjustedLabourPerGram.toFixed(2));
          totalAmount = (metalRateNum + adjustedLabour) * weightNum;
        }
      }
    }

    const newItem = {
      id: Date.now(),
      name: itemDetails.name,
      huid: itemDetails.huid,
      weight: parseFloat(itemDetails.weight) || 0,
      metalRate: parseFloat(itemDetails.metalRate) || 0,
      wastage: adjustedWastage,
      labour: adjustedLabour,
      total: totalAmount,
      currentWastage: wastageValue,
      currentLabour: labourValue,
      currentTotal: currentAmount,
      gstAdjusted: showGST && gstMode === "adjust",
      calculationType: itemDetails.calculationType,
      adjustWastage: true,
      itemObject: null,
      manualItemDetails: {
        ...itemDetails,
        wastage: adjustedWastage,
        labour: adjustedLabour,
      },
    };

    setItems((prev) => [...prev, newItem]);

    let message = `${itemDetails.name} added successfully!`;
    if (newItem.gstAdjusted) {
      message += ` Making charges adjusted from ${wastageValue}% to ${adjustedWastage.toFixed(
        2
      )}% to include GST`;
    }
    alert(message);
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);

    const weightAdjustments = [];
    for (const item of items) {
      if (item.weightAdjusted && item.itemObject) {
        weightAdjustments.push({
          originalItem: item.itemObject,
          adjustedWeight: item.weight,
          adjustmentData: item.weightAdjustmentData
        });
      }
    }
    let customerId = selectedCustomer?.id;

    // Handle new customer
    if (isNewCustomer && !selectedCustomer) {
      try {
        const formatDate = (date) => {
          if (!date) return "";
          try {
            return format(date, "dd/MM/yyyy");
          } catch {
            return "";
          }
        };

        const newCustomer = {
          name: values.customerName,
          contact: values.newCustomerContact,
          address: values.newCustomerAddress,
          dob: formatDate(values.newCustomerDob) || "",
          anniversary: formatDate(values.newCustomerAnniversary) || "",
          dateAdded: new Date().toISOString().split("T")[0],
        };

        const result = await addCustomer(newCustomer);
        customerId = result?.data?.id;
        setSelectedCustomer({ id: customerId, ...newCustomer });
      } catch (error) {
        console.error("Failed to add new customer:", error);
        alert("Failed to add new customer");
        return;
      }
    }

    // Get last bill number
    const lastTransaction = getLastCustomerTransaction();
    let billNo = 1;

    if (showGST) {
      billNo = (lastTransaction?.gst?.transactionDetails?.billNo || 0) + 1;
    } else {
      billNo = (lastTransaction?.nonGst?.transactionDetails?.billNo || 0) + 1;
    }

    let d = new Date(values.transactionDate);
    let month = "" + (d.getMonth() + 1);
    let year = d.getFullYear();
    let date = "" + d.getDate();
    let itemToUpdateId = [];

    const itemsToSave =
      transactionType === "detailed"
        ? items.map((item, index) => {
          const { id, itemObject, ...itemData } = item;
          let formattedName = itemData.name;
          let itemJson = itemObject || {};
          itemJson.sold = "Yes";

          if (itemJson?.id) {
            itemToUpdateId.push(itemJson.id);
          }

          if (itemObject) {
            let carat = "";
            if (itemObject?.melting === 92 || itemObject?.melting === 916) {
              carat = "22K";
            } else if (
              itemObject?.melting === 75 ||
              itemObject?.melting === 750
            ) {
              carat = "18K";
            } else if (
              itemObject?.melting === 58 ||
              itemObject?.melting === 585
            ) {
              carat = "14K";
            } else if (
              itemObject?.melting === 50 ||
              itemObject?.melting === 500
            ) {
              carat = "12K";
            }
            formattedName = `${itemObject.name || itemData.name} - ${itemObject.serialNo || ""
              } ${itemObject.metal} ${carat || ""}`;
          } else if (itemData.name) {
            formattedName = itemData.name;
          }

          return {
            itemNumber: index + 1,
            ...itemData,
            name: formattedName,
          };
        })
        : [];

    // Prepare transaction data
    const transactionData = {
      customer: customerId,
      customerName: values.customerName,
      date: `'${date}/${month}/${year}`,
      transactionDetails: {
        type: transactionType,
        billNo,
        gstApplicable: showGST,
        gstMode: showGST ? gstMode : null,
        gstType: showGST ? gstType : "cgst_sgst",
      },
      calculatedTotals: {
        subtotal: parseFloat(parseFloat(calculations.subtotal).toFixed(2)),
        totalItems: items.length.toString(),
      },
      paymentDetails: {
        cash: parseFloat(parseFloat(values.cashPayment).toFixed(2)) || 0,
        online: parseFloat(parseFloat(values.onlinePayment).toFixed(2)) || 0,
        cheque: parseFloat(parseFloat(values.chequePayment).toFixed(2)) || 0,
        other: parseFloat(parseFloat(values.otherPayment).toFixed(2)) || 0,
        advancePayment: {
          enabled: enableAdvancePayment,
          total: parseFloat(parseFloat(advanceTotal).toFixed(2)) || 0,
          breakup: advancePayments,
        },
        totalPaymentDone:
          parseFloat(parseFloat(values.paymentDone).toFixed(2)) || 0,
        discount: parseFloat(parseFloat(values.discount).toFixed(2)) || 0,
      },
      metaDetails: {
        handledBy: values.handledBy,
        gstNumber: values.gstNumber,
        panNumber: values.panNumber,
      },
      items: itemsToSave,
      totalAmount:
        transactionType === "total"
          ? parseFloat(values.totalAmount).toFixed(2) || 0
          : 0,
      summary: {
        grandTotal:
          parseFloat(parseFloat(calculations.grandTotal).toFixed(2)) || 0,
        paymentDone: parseFloat(parseFloat(values.paymentDone).toFixed(2)) || 0,
        pendingAmount: parseFloat(parseFloat(pendingAmount).toFixed(2)) || 0,
        cgst: parseFloat(parseFloat(calculations.cgst).toFixed(2)) || 0,
        sgst: parseFloat(parseFloat(calculations.sgst).toFixed(2)) || 0,
        igst: parseFloat(parseFloat(calculations.igst).toFixed(2)) || 0,
      },
    };

    try {
      await addCustomerTransactionAndUpdateStock(
        transactionData,
        itemToUpdateId
      );
      setIsTransactionSaved(true);
      alert("Transaction added successfully!");
    } catch (error) {
      console.error("Failed to add transaction:", error);
      alert("Failed to add transaction: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Desktop printing function
  const handleDesktopPrint = useCallback(
    (billHTML, styles) => {
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        alert("Please allow popups to print the bill");
        return;
      }

      printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Bill - ${showGST ? "GST Invoice" : "Cash Memo"}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <style>
          ${styles}
          
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            table {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            
            tr {
              page-break-inside: avoid !important;
              page-break-after: auto !important;
              break-inside: avoid !important;
            }
            
            thead {
              display: table-header-group !important;
            }
            
            tfoot {
              display: table-footer-group !important;
            }
          }
          
          .watermark {
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) rotate(-45deg) !important;
            opacity: 0.1 !important;
            z-index: -1 !important;
            pointer-events: none !important;
          }
          
          .watermark img {
            width: 400px !important;
            height: 400px !important;
            object-fit: contain !important;
          }
        </style>
      </head>
      <body>
        ${billHTML}
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                setTimeout(() => window.close(), 100);
              };
            }, 500);
          };
        </script>
      </body>
    </html>
  `);

      printWindow.document.close();
    },
    [showGST]
  );

  // Mobile-optimized printing function
  const handleMobilePrint = useCallback(
    (billHTML, styles) => {
      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.style.visibility = "hidden";

      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentWindow.document;

      iframeDoc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print Bill - ${showGST ? "GST Invoice" : "Cash Memo"}</title>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes">
        <style>
          ${styles}
          
          @media print {
            body {
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            #billContent, #billContentWithoutGst {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              border: 2px solid #2c3e50 !important;
            }
            
            table {
              width: 100% !important;
              table-layout: fixed !important;
            }
            
            td, th {
              word-wrap: break-word !important;
              white-space: normal !important;
            }
          }
          
          .print-loading {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: #2c3e50;
            color: white;
            padding: 15px 30px;
            border-radius: 8px;
            font-family: Arial, sans-serif;
            z-index: 9999;
            text-align: center;
            box-shadow: 0 2px 10px rgba(0,0,0,0.2);
          }
          
          .print-loading:after {
            content: '';
            display: inline-block;
            width: 20px;
            height: 20px;
            margin-left: 10px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #d4af37;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            vertical-align: middle;
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        </style>
      </head>
      <body>
        <div class="print-loading">Preparing bill for printing...</div>
        ${billHTML}
        <script>
          window.onload = function() {
            const loadingEl = document.querySelector('.print-loading');
            if (loadingEl) {
              loadingEl.style.display = 'none';
            }
            
            setTimeout(function() {
              try {
                window.print();
              } catch(e) {
                console.error('Print error:', e);
                alert('Unable to print. Please try again or use a different browser.');
              }
              
              window.onafterprint = function() {
                setTimeout(function() {
                  window.parent.document.body.removeChild(window.frameElement);
                }, 100);
              };
              
              setTimeout(function() {
                if (window.parent && window.frameElement) {
                  window.parent.document.body.removeChild(window.frameElement);
                }
              }, 30000);
            }, 1000);
          };
        </script>
      </body>
    </html>
  `);

      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow.focus();
      }, 500);
    },
    [showGST]
  );

  const handlePrintBill = useCallback(() => {
    if (!formik.values.customerName.trim()) {
      alert("Please select or enter a customer name");
      return;
    }

    if (!formik.values.handledBy.trim()) {
      alert("Please enter seller name");
      return;
    }

    if (
      transactionType === "total" &&
      (!formik.values.totalAmount || parseFloat(formik.values.totalAmount) <= 0)
    ) {
      alert("Please enter a valid total amount");
      return;
    }

    const lastTransaction = getLastCustomerTransaction();
    let billNo = 1;
    if (showGST) {
      billNo = (lastTransaction?.gst?.transactionDetails?.billNo || 0) + 1;
    } else {
      billNo = (lastTransaction?.nonGst?.transactionDetails?.billNo || 0) + 1;
    }

    let d = new Date(formik.values.transactionDate);
    let month = "" + (d.getMonth() + 1);
    if (month.length === 1) month = "0" + month;
    let year = d.getFullYear();
    let date = "" + d.getDate();
    if (date.length === 1) date = "0" + date;

    const itemsForBill =
      transactionType === "detailed"
        ? items.map((item, index) => {
          const { id, itemObject, ...itemData } = item;
          let displayName = itemData.name;
          if (itemObject) {
            const itemObj = parseItemObject(itemObject);
            let carat = "";
            if (itemObj?.melting === 92 || itemObj?.melting === 916) {
              carat = "22K";
            } else if (itemObj?.melting === 75 || itemObj?.melting === 750) {
              carat = "18K";
            } else if (itemObj?.melting === 58 || itemObj?.melting === 585) {
              carat = "14K";
            } else if (itemObj?.melting === 50 || itemObj?.melting === 500) {
              carat = "12K";
            }
            displayName = `${itemObj.name || itemData.name} - ${itemObj.serialNo || ""
              } ${itemObj.metal || ""} ${carat || ""}`;
          }
          return {
            ...itemData,
            name: displayName,
            itemObject: itemObject || null,
          };
        })
        : [];

    const advanceTotalForBill = enableAdvancePayment
      ? Object.values(advancePayments).reduce(
        (sum, val) => sum + (parseFloat(val) || 0),
        0
      )
      : 0;

    const transactionDataForBill = {
      transactionDetails: {
        type: transactionType,
        billNo,
        gstApplicable: showGST,
        gstMode: showGST ? gstMode : null,
        gstType: showGST ? gstType : "cgst_sgst",
      },
      date: `'${date}/${month}/${year}`,
      calculatedTotals: {
        subtotal: calculations.subtotal,
      },
      paymentDetails: {
        cash: parseFloat(formik.values.cashPayment) || 0,
        online: parseFloat(formik.values.onlinePayment) || 0,
        cheque: parseFloat(formik.values.chequePayment) || 0,
        other: parseFloat(formik.values.otherPayment) || 0,
        advancePayment: {
          enabled: enableAdvancePayment,
          total: advanceTotalForBill,
          breakup: advancePayments,
        },
        discount: parseFloat(formik.values.discount) || 0,
      },
      metaDetails: {
        handledBy: formik.values.handledBy,
        gstNumber: formik.values.gstNumber || "",
        panNumber: formik.values.panNumber || "",
      },
      totalAmount:
        transactionType === "total"
          ? parseFloat(formik.values.totalAmount) || 0
          : 0,
      summary: {
        grandTotal: calculations.grandTotal,
        paymentDone: parseFloat(formik.values.paymentDone) || 0,
        pendingAmount: pendingAmount > 0 ? pendingAmount : 0,
        cgst: calculations.cgst,
        sgst: calculations.sgst,
        igst: calculations.igst,
      },
    };

    const billData = {
      type: showGST ? "gst" : "non-gst",
      data: transactionDataForBill,
      items: itemsForBill,
      payments: transactionDataForBill.paymentDetails,
      customer: selectedCustomer || {
        name: formik.values.customerName,
        contact: formik.values.newCustomerContact || "",
        address: formik.values.newCustomerAddress || "",
      },
      advanceData: advancePayments,
    };

    const billHTML = generateBillHTML(billData);
    const styles = getBillStyles();

    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    if (isMobile) {
      handleMobilePrint(billHTML, styles);
    } else {
      handleDesktopPrint(billHTML, styles);
    }
  }, [
    formik.values,
    selectedCustomer,
    showGST,
    gstMode,
    gstType,
    transactionType,
    items,
    enableAdvancePayment,
    advancePayments,
    calculations,
    pendingAmount,
    getLastCustomerTransaction,
    handleDesktopPrint,
    handleMobilePrint,
  ]);

  const resetForm = useCallback(() => {
    formik.resetForm();
    setIsTransactionSaved(false);
    setSelectedCustomer(null);
    setCustomerQuery("");
    setIsNewCustomer(false);
    setShowNewCustomerFields(false);
    setItems([]);
    setTransactionType("detailed");
    setShowGST(false);
    setGstMode("add");
    setGstType("cgst_sgst");
    setEnableAdvancePayment(false);
    setPaymentModes({
      cash: false,
      online: false,
      oldGold: false,
      oldSilver: false,
      other: false,
    });
    setAdvancePayments({});
    setOtherOptions([]);
    setManualItemName("");
    setManualSerial("");
  }, [formik]);

  // UI rendering functions
  const renderItemRows = useMemo(
    () =>
      items.map((item) => {
        const displayWeight = item.weight || 0;
        const displayMetalRate = item.metalRate || '';
        const displayWastage = item.wastage || 0;
        const itemTotal = typeof item.total === 'number' ? item.total : parseFloat(item.total) || 0;
        const hasStockItem = !!item.itemObject;
        const isWeightAdjusted = item.weightAdjusted || item.adjustmentApplied;
        const needsAdjustment = pendingWeightAdjustments?.[item?.id]?.needsAdjustment;

        let displayName = item.name;
        if (item.itemObject) {
          const itemObj = parseItemObject(item.itemObject);
          let carat = "";
          if (itemObj?.melting === 92 || itemObj?.melting === 916) carat = "22K";
          else if (itemObj?.melting === 75 || itemObj?.melting === 750) carat = "18K";
          else if (itemObj?.melting === 58 || itemObj?.melting === 585) carat = "14K";
          else if (itemObj?.melting === 50 || itemObj?.melting === 500) carat = "12K";
          displayName = `${itemObj.name || item.name} - ${itemObj.serialNo || ""} ${itemObj.metal || ""} ${carat || ""}`;
        }

        return (
          <TableRow
            key={item.id}
            sx={{
              "&:last-child td, &:last-child th": { border: 0 },
              backgroundColor: needsAdjustment ? alpha(theme.palette.warning.main, 0.1) : 'transparent',
              '&:hover': {
                backgroundColor: needsAdjustment ? alpha(theme.palette.warning.main, 0.15) : 'inherit',
              }
            }}
          >
            <TableCell>
              <TextField
                value={displayName}
                onChange={(e) => updateItemField(item.id, "name", e.target.value)}
                fullWidth
                variant="standard"
              />
            </TableCell>
            <TableCell>
              <TextField
                value={item.huid}
                onChange={(e) => updateItemField(item.id, "huid", e.target.value)}
                fullWidth
                size="small"
                variant="standard"
              />
            </TableCell>
            <TableCell>
              <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <TextField
                  type="text"
                  value={displayWeight}
                  onClick={() => hasStockItem && openWeightEditDialog(item)}
                  fullWidth
                  size="small"
                  variant="standard"
                  inputProps={{
                    step: "0.01",
                    readOnly: hasStockItem,
                    style: { cursor: hasStockItem ? 'pointer' : 'text' }
                  }}
                  sx={{
                    '& .MuiInput-root': {
                      '&::before': {
                        borderBottom: `1px solid ${alpha(
                          isDark ? theme.palette.divider : '#e2e8f0',
                          isDark ? 0.2 : 0.3
                        )}`,
                      },
                    },
                    '& .MuiInput-input': {
                      color: needsAdjustment ? theme.palette.warning.main : 'inherit',
                      fontWeight: needsAdjustment ? 600 : 'normal',
                      cursor: hasStockItem ? 'pointer' : 'text',
                    },
                    '& .MuiInput-input:hover': {
                      cursor: hasStockItem ? 'pointer' : 'text',
                    }
                  }}
                />
                {hasStockItem && (
                  <IconButton
                    size="small"
                    onClick={() => openWeightEditDialog(item)}
                    sx={{ ml: 1, p: 0.5 }}
                    title="Click to edit weight"
                  >
                    <EditIcon fontSize="small" sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
                {needsAdjustment && (
                  <Chip
                    label="Pending"
                    size="small"
                    color="warning"
                    sx={{
                      position: 'absolute',
                      right: -60,
                      top: -8,
                      height: 20,
                      fontSize: '0.6rem',
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>
            </TableCell>
            <TableCell>
              <TextField
                type="text"
                value={displayMetalRate}
                onChange={(e) => updateItemField(item.id, "metalRate", e.target.value)}
                fullWidth
                size="small"
                variant="standard"
              />
            </TableCell>
            <TableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  type="text"
                  value={displayWastage}
                  onChange={(e) => updateItemField(item.id, "wastage", e.target.value)}
                  fullWidth
                  size="small"
                  variant="standard"
                  disabled={!item.adjustWastage}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={item.adjustWastage !== false}
                      onChange={(e) => {
                        const newAdjustWastage = e.target.checked;
                        setItems(prev =>
                          prev.map(i =>
                            i.id === item.id
                              ? {
                                ...i,
                                adjustWastage: newAdjustWastage,
                                wastage: newAdjustWastage ? i.wastage : 0
                              }
                              : i
                          )
                        );
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                      Adj
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            </TableCell>
            <TableCell>
              <TextField
                type="text"
                value={itemTotal.toFixed(2)}
                onChange={(e) => updateItemField(item.id, "total", e.target.value)}
                fullWidth
                size="small"
                variant="standard"
              />
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1}>
                {hasStockItem && (
                  <IconButton
                    onClick={() => openWeightEditDialog(item)}
                    size="small"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: needsAdjustment
                        ? alpha(theme.palette.warning.main, isDark ? 0.3 : 0.2)
                        : alpha(theme.palette.info.main, isDark ? 0.2 : 0.1),
                      color: needsAdjustment ? theme.palette.warning.main : theme.palette.info.main,
                      border: `1px solid ${alpha(
                        needsAdjustment ? theme.palette.warning.main : theme.palette.info.main,
                        isDark ? 0.3 : 0.2
                      )}`,
                      '&:hover': {
                        backgroundColor: alpha(
                          needsAdjustment ? theme.palette.warning.main : theme.palette.info.main,
                          isDark ? 0.4 : 0.25
                        ),
                      }
                    }}
                    title={needsAdjustment ? "Review weight adjustment" : "Edit weight"}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
                {isWeightAdjusted && !needsAdjustment && (
                  <Chip
                    label="Adjusted"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ height: 24, fontSize: '0.7rem' }}
                  />
                )}
                <IconButton
                  onClick={() => removeItemRow(item.id)}
                  size="small"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.error.main, isDark ? 0.2 : 0.1),
                    color: theme.palette.error.main,
                    border: `1px solid ${alpha(theme.palette.error.main, isDark ? 0.3 : 0.2)}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, isDark ? 0.3 : 0.15),
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            </TableCell>
          </TableRow>
        );
      }),
    [items, updateItemField, removeItemRow, isDark, theme, pendingWeightAdjustments, openWeightEditDialog]
  );

  const renderCustomerSuggestions = useMemo(
    () =>
      customerSuggestions.map((customer) => (
        <Box
          key={customer.id}
          sx={{
            p: 1.5,
            backgroundColor: alpha(
              isDark ? theme.palette.background.paper : '#ffffff',
              isDark ? 0.3 : 0.6
            ),
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(
              isDark ? theme.palette.divider : '#e2e8f0',
              isDark ? 0.2 : 0.3
            )}`,
            borderRadius: 1,
            mb: 1,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(
                isDark ? theme.palette.background.paper : '#ffffff',
                isDark ? 0.5 : 0.8
              ),
              borderColor: alpha(
                isDark ? '#60a5fa' : '#3b82f6',
                isDark ? 0.4 : 0.5
              ),
              transform: 'translateX(2px)',
            },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          onClick={() => handleCustomerSelect(customer)}
        >
          {customer.isNew ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                color: isDark ? '#60a5fa' : '#3b82f6',
              }}
            >
              <AddIcon sx={{ mr: 1, fontSize: 16 }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Add "{customerQuery}" as new customer
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Person sx={{ fontSize: 16, color: isDark ? '#cbd5e1' : '#64748b' }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {customer.name}
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#94a3b8' }}>
                  ({customer.contact})
                </Typography>
              </Box>
              {(customer.gstNo || customer.panNo) && (
                <Box sx={{ display: 'flex', gap: 1, ml: 3 }}>
                  {customer.gstNo && (
                    <Typography variant="caption" sx={{
                      color: isDark ? '#60a5fa' : '#3b82f6',
                      fontSize: '0.65rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <AccountBalanceIcon sx={{ fontSize: 12 }} />
                      GST: {customer.gstNo}
                    </Typography>
                  )}
                  {customer.panNo && (
                    <Typography variant="caption" sx={{
                      color: isDark ? '#94a3b8' : '#64748b',
                      fontSize: '0.65rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                      <InfoIcon sx={{ fontSize: 12 }} />
                      PAN: {customer.panNo}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )),
    [customerSuggestions, customerQuery, handleCustomerSelect, isDark, theme]
  );

  // Show skeleton during initial loading OR actual data loading
  if (initialLoading || isLoading) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ minHeight: "100vh", py: 3 }}>
          <Box sx={{ maxWidth: "1400px", mx: "auto", px: 2 }}>
            {/* HEADER SKELETON */}
            <Stack direction="row" alignItems="center" spacing={2} mb={4}>
              <Skeleton variant="text" width={300} height={48} />
            </Stack>

            {/* MAIN 3-COLUMN GRID SKELETON */}
            <Grid container spacing={3} alignItems="flex-start">
              {/* LEFT COLUMN SKELETON */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <CustomerInfoSkeleton isDark={isDark} />
              </Grid>

              {/* CENTER COLUMN SKELETON */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <Stack spacing={3}>
                  <ItemsSectionSkeleton isDark={isDark} />
                  <GstSectionSkeleton isDark={isDark} />
                  <PaymentSectionSkeleton isDark={isDark} />
                </Stack>
              </Grid>

              {/* RIGHT COLUMN SKELETON */}
              <Grid item size={{ xs: 12, md: 12 }}>
                <Stack spacing={3} sx={{ position: "sticky", top: 24 }}>
                  <SummarySkeleton isDark={isDark} />
                  <ActionButtonsSkeleton isDark={isDark} />
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          minHeight: "100vh",
          py: 3,
        }}
      >
        <ManualItemDialog
          open={manualItemDialogOpen}
          onClose={() => setManualItemDialogOpen(false)}
          onSubmit={handleAddManualItem}
          manualItemDetails={manualItemDetails}
          setManualItemDetails={setManualItemDetails}
        />

        <Box
          sx={{
            maxWidth: "1400px",
            mx: "auto",
            px: 2,
          }}
        >
          {/* HEADER */}
          <Stack direction="row" alignItems="center" spacing={2} mb={4}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                background: isDark
                  ? "linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)"
                  : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
              }}
            >
              New Customer Transaction
            </Typography>
          </Stack>

          {/* MAIN 3-COLUMN GRID */}
          <Grid container spacing={3} alignItems="flex-start">
            {/* LEFT COLUMN — CUSTOMER INFORMATION PANEL */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backdropFilter: "blur(20px)",
                  backgroundColor: alpha(
                    isDark ? theme.palette.background.paper : "#ffffff",
                    isDark ? 0.3 : 0.6
                  ),
                  border: `1px solid ${alpha(
                    isDark ? theme.palette.divider : "#e2e8f0",
                    isDark ? 0.2 : 0.3
                  )}`,
                  boxShadow: theme.shadows[4],
                }}
              >
                {/* PANEL HEADER */}
                <Stack direction="row" alignItems="center" spacing={1} mb={3}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: isDark
                        ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                        : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "1rem",
                    }}
                  >
                    1
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: isDark ? "#e2e8f0" : "#1e293b",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Person fontSize="small" />
                    Customer Information
                  </Typography>
                </Stack>

                {/* SEARCH CUSTOMER */}
                <Stack spacing={2}>
                  <TextField
                    label="Search Customer"
                    placeholder="Type name or contact..."
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="medium"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backdropFilter: "blur(10px)",
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : "#ffffff",
                          isDark ? 0.2 : 0.7
                        ),
                        "&:hover": {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.3 : 0.8
                          ),
                        },
                        "&.Mui-focused": {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.3 : 0.8
                          ),
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: isDark ? "#cbd5e1" : "#64748b",
                      },
                      "& .MuiOutlinedInput-input": {
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon
                            fontSize="small"
                            sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* SUGGESTIONS DROPDOWN */}
                  {customerSuggestions.length > 0 && (
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1,
                        borderRadius: 2,
                        backdropFilter: "blur(20px)",
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : "#ffffff",
                          isDark ? 0.3 : 0.85
                        ),
                        border: `1px solid ${alpha(
                          isDark ? theme.palette.divider : "#e2e8f0",
                          isDark ? 0.2 : 0.3
                        )}`,
                        boxShadow: theme.shadows[4],
                        overflow: "hidden",
                        maxHeight:
                          customerSuggestions.length > 5 ? "260px" : "auto",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* DROPDOWN HEADER */}
                      <Box
                        sx={{
                          p: 1.5,
                          borderBottom: `1px solid ${alpha(
                            isDark ? theme.palette.divider : "#e2e8f0",
                            isDark ? 0.1 : 0.2
                          )}`,
                          backgroundColor: alpha(
                            isDark ? "#1e293b" : "#f8fafc",
                            isDark ? 0.4 : 0.5
                          ),
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? "#cbd5e1" : "#475569",
                            fontSize: "0.75rem",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <SearchIcon fontSize="inherit" />
                          {customerSuggestions.length} suggestion
                          {customerSuggestions.length !== 1 ? "s" : ""} found
                        </Typography>
                      </Box>

                      {/* SCROLLABLE SUGGESTIONS LIST */}
                      <Box
                        sx={{
                          overflowY: "auto",
                          maxHeight: "210px",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: alpha(
                              isDark ? "#1e293b" : "#f1f5f9",
                              isDark ? 0.3 : 0.5
                            ),
                            borderRadius: "3px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: alpha(
                              isDark ? "#60a5fa" : "#3b82f6",
                              isDark ? 0.4 : 0.5
                            ),
                            borderRadius: "3px",
                            "&:hover": {
                              backgroundColor: alpha(
                                isDark ? "#3b82f6" : "#2563eb",
                                isDark ? 0.6 : 0.7
                              ),
                            },
                          },
                        }}
                      >
                        {renderCustomerSuggestions}
                      </Box>

                      {/* SHOW MORE INDICATOR */}
                      {customerSuggestions.length > 5 && (
                        <Box
                          sx={{
                            p: 1,
                            borderTop: `1px solid ${alpha(
                              isDark ? theme.palette.divider : "#e2e8f0",
                              isDark ? 0.1 : 0.2
                            )}`,
                            textAlign: "center",
                            backgroundColor: alpha(
                              isDark ? "#1e293b" : "#f8fafc",
                              isDark ? 0.4 : 0.5
                            ),
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              fontSize: "0.7rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <span>
                              Scroll to see {customerSuggestions.length - 5}{" "}
                              more
                            </span>
                            <span style={{ transform: "rotate(90deg)" }}>
                              ›
                            </span>
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  )}

                  {/* NEW CUSTOMER INFO ALERT */}
                  {isNewCustomer &&
                    !selectedCustomer &&
                    customerQuery.trim() && (
                      <Alert
                        severity="info"
                        icon={<InfoIcon fontSize="small" />}
                        sx={{
                          borderRadius: 2,
                          alignItems: "center",
                          py: 1,
                          backdropFilter: "blur(10px)",
                          backgroundColor: alpha(
                            isDark
                              ? theme.palette.info.main
                              : theme.palette.info.main,
                            isDark ? 0.2 : 0.1
                          ),
                          border: `1px solid ${alpha(
                            isDark
                              ? theme.palette.info.main
                              : theme.palette.info.main,
                            isDark ? 0.3 : 0.2
                          )}`,
                          "& .MuiAlert-message": {
                            flex: 1,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: isDark ? "#e2e8f0" : "#1e293b",
                            }}
                          >
                            Customer not found. Add "{customerQuery}" as new
                            customer.
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              formik.setFieldValue(
                                "customerName",
                                customerQuery
                              );
                              formik.setFieldValue("isNewCustomer", true);
                              setShowNewCustomerFields(true);
                              setIsNewCustomer(true);
                              setSelectedCustomer(null);
                              setCustomerSuggestions([]);
                            }}
                            sx={{
                              ml: 2,
                              borderRadius: 1,
                              fontSize: "0.75rem",
                              py: 0.25,
                              backdropFilter: "blur(10px)",
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.3 : 0.8
                              ),
                              borderColor: alpha(
                                isDark ? "#60a5fa" : "#3b82f6",
                                isDark ? 0.4 : 0.5
                              ),
                              color: isDark ? "#60a5fa" : "#3b82f6",
                              "&:hover": {
                                backgroundColor: alpha(
                                  isDark
                                    ? theme.palette.background.paper
                                    : "#ffffff",
                                  isDark ? 0.5 : 0.9
                                ),
                                borderColor: alpha(
                                  isDark ? "#60a5fa" : "#3b82f6",
                                  isDark ? 0.6 : 0.7
                                ),
                              },
                            }}
                          >
                            Add Now
                          </Button>
                        </Box>
                      </Alert>
                    )}
                </Stack>

                {/* SELECTED CUSTOMER BANNER */}
                {selectedCustomer && (
                  <Paper
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 2,
                      backdropFilter: "blur(20px)",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%)"
                        : "linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.08) 100%)",
                      border: `1px solid ${alpha(
                        isDark ? "#10b981" : "#059669",
                        isDark ? 0.25 : 0.2
                      )}`,
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        width: "60px",
                        height: "60px",
                        background: isDark
                          ? "linear-gradient(135deg, transparent 50%, rgba(16, 185, 129, 0.1) 50%)"
                          : "linear-gradient(135deg, transparent 50%, rgba(5, 150, 105, 0.1) 50%)",
                      }}
                    />

                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      position="relative"
                      zIndex={1}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isDark
                            ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                            : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          color: "white",
                        }}
                      >
                        <Person fontSize="small" />
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            mb: 0.5,
                          }}
                        >
                          {selectedCustomer.name}
                          {selectedCustomer.contact && (
                            <Typography
                              component="span"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                ml: 1,
                                fontWeight: 400,
                              }}
                            >
                              ({selectedCustomer.contact})
                            </Typography>
                          )}
                        </Typography>

                        {selectedCustomer.address && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#cbd5e1" : "#475569",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Home fontSize="inherit" />
                            {selectedCustomer.address}
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedCustomer(null);
                          setCustomerQuery("");
                          formik.setFieldValue("customerName", "");
                        }}
                        sx={{
                          borderRadius: 1.5,
                          backdropFilter: "blur(10px)",
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : "#e2e8f0",
                            isDark ? 0.2 : 0.3
                          )}`,
                          color: isDark ? "#cbd5e1" : "#64748b",
                          "&:hover": {
                            backgroundColor: alpha(
                              isDark
                                ? theme.palette.background.paper
                                : "#ffffff",
                              isDark ? 0.5 : 0.9
                            ),
                            color: isDark ? "#ef4444" : "#dc2626",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Paper>
                )}

                {/* NEW CUSTOMER DETAILS */}
                {showNewCustomerFields && (
                  <Card
                    sx={{
                      mt: 3,
                      p: 2.5,
                      borderRadius: 2,
                      backdropFilter: "blur(10px)",
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : "#ffffff",
                        isDark ? 0.3 : 0.7
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : "#e2e8f0",
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: "none",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: isDark ? "#e2e8f0" : "#1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Person fontSize="small" />
                      New Customer Details
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Contact Number *"
                          name="newCustomerContact"
                          value={formik.values.newCustomerContact}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.newCustomerContact &&
                            Boolean(formik.errors.newCustomerContact)
                          }
                          helperText={
                            formik.touched.newCustomerContact &&
                            formik.errors.newCustomerContact
                          }
                          fullWidth
                          size="small"
                          required
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.2 : 0.7
                              ),
                              "&:hover": {
                                backgroundColor: alpha(
                                  isDark
                                    ? theme.palette.background.paper
                                    : "#ffffff",
                                  isDark ? 0.3 : 0.8
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Phone
                                  fontSize="small"
                                  sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Address"
                          name="newCustomerAddress"
                          value={formik.values.newCustomerAddress}
                          onChange={formik.handleChange}
                          multiline
                          rows={2}
                          fullWidth
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1.5,
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.2 : 0.7
                              ),
                              "&:hover": {
                                backgroundColor: alpha(
                                  isDark
                                    ? theme.palette.background.paper
                                    : "#ffffff",
                                  isDark ? 0.3 : 0.8
                                ),
                              },
                            },
                            "& .MuiOutlinedInput-input": {
                              color: isDark ? "#e2e8f0" : "#1e293b",
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Home
                                  fontSize="small"
                                  sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Date of Birth"
                          value={formik.values.newCustomerDob}
                          onChange={(date) =>
                            formik.setFieldValue("newCustomerDob", date)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark
                                      ? theme.palette.background.paper
                                      : "#ffffff",
                                    isDark ? 0.2 : 0.7
                                  ),
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      isDark
                                        ? theme.palette.background.paper
                                        : "#ffffff",
                                      isDark ? 0.3 : 0.8
                                    ),
                                  },
                                },
                                "& .MuiOutlinedInput-input": {
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                },
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Anniversary"
                          value={formik.values.newCustomerAnniversary}
                          onChange={(date) =>
                            formik.setFieldValue("newCustomerAnniversary", date)
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  backgroundColor: alpha(
                                    isDark
                                      ? theme.palette.background.paper
                                      : "#ffffff",
                                    isDark ? 0.2 : 0.7
                                  ),
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      isDark
                                        ? theme.palette.background.paper
                                        : "#ffffff",
                                      isDark ? 0.3 : 0.8
                                    ),
                                  },
                                },
                                "& .MuiOutlinedInput-input": {
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                },
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Card>
                )}

                {/* SELLER & BASE FIELDS */}
                <Divider
                  sx={{
                    my: 3,
                    borderColor: alpha(
                      isDark ? theme.palette.divider : "#e2e8f0",
                      isDark ? 0.1 : 0.2
                    ),
                  }}
                />

                <Stack spacing={2}>
                  <TextField
                    label="Seller Name *"
                    name="handledBy"
                    value={formik.values.handledBy}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.handledBy &&
                      Boolean(formik.errors.handledBy)
                    }
                    helperText={
                      formik.touched.handledBy && formik.errors.handledBy
                    }
                    fullWidth
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backdropFilter: "blur(10px)",
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : "#ffffff",
                          isDark ? 0.3 : 0.8
                        ),
                        "&:hover": {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.4 : 0.85
                          ),
                        },
                        "&.Mui-focused": {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.4 : 0.85
                          ),
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: isDark ? "#cbd5e1" : "#64748b",
                      },
                      "& .MuiOutlinedInput-input": {
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      },
                    }}
                  />

                  <DatePicker
                    label="Transaction Date *"
                    value={formik.values.transactionDate}
                    onChange={(date) =>
                      formik.setFieldValue("transactionDate", date)
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backdropFilter: "blur(10px)",
                            backgroundColor: alpha(
                              isDark
                                ? theme.palette.background.paper
                                : "#ffffff",
                              isDark ? 0.3 : 0.8
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.4 : 0.85
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                          },
                        },
                      },
                    }}
                  />

                  <TextField
                    label="GST Number"
                    name="gstNumber"
                    value={formik.values.gstNumber}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backdropFilter: "blur(10px)",
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : "#ffffff",
                          isDark ? 0.3 : 0.8
                        ),
                        "&:hover": {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.4 : 0.85
                          ),
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      },
                    }}
                  />

                  <TextField
                    label="PAN Number"
                    name="panNumber"
                    value={formik.values.panNumber}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backdropFilter: "blur(10px)",
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : "#ffffff",
                          isDark ? 0.3 : 0.8
                        ),
                        "&:hover": {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.4 : 0.85
                          ),
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      },
                    }}
                  />
                </Stack>
              </Card>
            </Grid>

            {/* CENTER COLUMN — ITEMS • GST • PAYMENT PANEL */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Stack spacing={3}>
                {/* DETAILED ITEM ENTRY SECTION */}
                {transactionType === "detailed" && (
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backdropFilter: "blur(20px)",
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : "#ffffff",
                        isDark ? 0.3 : 0.6
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : "#e2e8f0",
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    {/* HEADER */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <ShoppingBag fontSize="small" />
                        Purchase Items
                      </Typography>

                      <Stack direction="row" spacing={2}>
                        <Button
                          startIcon={<AddIcon />}
                          variant="contained"
                          size="medium"
                          onClick={() => setManualItemDialogOpen(true)}
                          sx={{
                            borderRadius: 2,
                            background: isDark
                              ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                              : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.25)",
                            "&:hover": {
                              background: isDark
                                ? "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
                                : "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
                            },
                          }}
                        >
                          Add from Gross Stock
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<QrCode />}
                          onClick={() => setScannerOpen(true)}
                          sx={{
                            borderRadius: 2,
                            background: isDark
                              ? "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)"
                              : "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
                            boxShadow: "0 4px 12px rgba(139, 92, 246, 0.25)",
                            "&:hover": {
                              background: isDark
                                ? "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)"
                                : "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
                            },
                          }}
                        >
                          Scan QR
                        </Button>
                      </Stack>

                      <QRScanner
                        open={scannerOpen}
                        onClose={() => setScannerOpen(false)}
                        onScan={handleQRScan}
                      />
                    </Stack>

                    {/* QUICK ADD FROM STOCK */}
                    {/* QUICK ADD FROM STOCK */}
                    <Paper
                      sx={{
                        p: 2.5,
                        mb: 3,
                        borderRadius: 2,
                        backdropFilter: "blur(10px)",
                        backgroundColor: alpha(
                          isDark ? "#1e293b" : "#ffffff",
                          isDark ? 0.4 : 0.7
                        ),
                        border: `1px solid ${alpha(
                          isDark ? "#334155" : "#e2e8f0",
                          isDark ? 0.2 : 0.3
                        )}`,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <InventoryIcon fontSize="small" />
                        Quick Add from Stock
                      </Typography>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={3} sx={{ width: "250px" }}>
                          <Autocomplete
                            options={itemCategories}
                            getOptionLabel={(option) => {
                              if (typeof option === "string") return option;
                              return `${option.name} (${option.shortcut})`;
                            }}
                            value={
                              itemCategories.find(
                                (i) => i.name === manualItemName
                              ) || null
                            }
                            onChange={(event, newValue, reason) => {
                              if (reason === "clear") {
                                setManualItemName("");
                                return;
                              }

                              if (newValue) {
                                setManualItemName(newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Item Type"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                  }}
                                >
                                  <Typography variant="body2">
                                    {option.name}
                                  </Typography>
                                  <Chip
                                    label={option.shortcut}
                                    size="small"
                                    sx={{ ml: 1, fontSize: "0.7rem" }}
                                  />
                                </Box>
                              </li>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3} sx={{ width: "250px" }}>
                          <Autocomplete
                            options={metalTypes}
                            getOptionLabel={(option) => {
                              if (typeof option === "string") return option;
                              return `${option.name} (${option.shortcut})`;
                            }}
                            value={
                              metalTypes.find(
                                (m) => m.name === manualItemMetal
                              ) || null
                            }
                            onChange={(event, newValue, reason) => {
                              if (reason === "clear") {
                                setManualItemMetal("");
                                return;
                              }

                              if (newValue) {
                                setManualItemMetal(newValue.name);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Metal"
                                size="small"
                                fullWidth
                              />
                            )}
                            renderOption={(props, option) => (
                              <li {...props}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                  }}
                                >
                                  <Typography variant="body2">
                                    {option.name}
                                  </Typography>
                                  <Chip
                                    label={option.shortcut}
                                    size="small"
                                    sx={{ ml: 1, fontSize: "0.7rem" }}
                                  />
                                </Box>
                              </li>
                            )}
                          />
                        </Grid>

                        <Grid item xs={12} md={3} sx={{ width: "250px" }}>
                          <TextField
                            label="Serial No."
                            value={manualSerial}
                            onChange={(e) => setManualSerial(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1.5,
                                backdropFilter: "blur(10px)",
                                backgroundColor: alpha(
                                  isDark
                                    ? theme.palette.background.paper
                                    : "#ffffff",
                                  isDark ? 0.2 : 0.7
                                ),
                                "&:hover": {
                                  backgroundColor: alpha(
                                    isDark
                                      ? theme.palette.background.paper
                                      : "#ffffff",
                                    isDark ? 0.3 : 0.8
                                  ),
                                },
                              },
                              "& .MuiOutlinedInput-input": {
                                color: isDark ? "#e2e8f0" : "#1e293b",
                              },
                            }}
                          />
                        </Grid>

                        <Grid item xs={12} md={3}>
                          <Button
                            fullWidth
                            startIcon={<AddIcon />}
                            variant="contained"
                            onClick={handleAddBySerial}
                            sx={{
                              borderRadius: 1.5,
                              height: "40px",
                              width: "200px",
                              background: isDark
                                ? "linear-gradient(135deg, #10B981 0%, #059669 100%)"
                                : "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
                              "&:hover": {
                                background: isDark
                                  ? "linear-gradient(135deg, #059669 0%, #047857 100%)"
                                  : "linear-gradient(135deg, #059669 0%, #047857 100%)",
                              },
                            }}
                          >
                            Add from Stock
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>

                    {/* ITEM TABLE */}
                    {items.length > 0 ? (
                      <TableContainer
                        component={Paper}
                        sx={{
                          borderRadius: 2,
                          backdropFilter: "blur(10px)",
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.2 : 0.7
                          ),
                          border: `1px solid ${alpha(
                            isDark ? theme.palette.divider : "#e2e8f0",
                            isDark ? 0.2 : 0.3
                          )}`,
                          overflow: "hidden",
                        }}
                      >
                        <Table size="small">
                          <TableHead
                            sx={{
                              backgroundColor: alpha(
                                isDark ? "#1e293b" : "#f8fafc",
                                isDark ? 0.5 : 0.9
                              ),
                            }}
                          >
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                Item
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                HUID
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                Weight
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                Rate
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                Wastage
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                Total
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: isDark ? "#e2e8f0" : "#1e293b",
                                }}
                              >
                                Actions
                              </TableCell>
                            </TableRow>
                          </TableHead>

                          <TableBody>{renderItemRows}</TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Paper
                        sx={{
                          p: 3,
                          textAlign: "center",
                          borderRadius: 2,
                          backdropFilter: "blur(10px)",
                          backgroundColor: alpha(
                            isDark ? "#1e293b" : "#f8fafc",
                            isDark ? 0.3 : 0.7
                          ),
                          border: `1px dashed ${alpha(
                            isDark ? theme.palette.divider : "#cbd5e1",
                            isDark ? 0.2 : 0.4
                          )}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: isDark ? "#94a3b8" : "#64748b" }}
                        >
                          No items added yet.
                        </Typography>
                      </Paper>
                    )}
                  </Card>
                )}

                {/* TOTAL AMOUNT (NON-ITEM) */}
                {transactionType === "total" && (
                  <Card
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      backdropFilter: "blur(20px)",
                      backgroundColor: alpha(
                        isDark ? theme.palette.background.paper : "#ffffff",
                        isDark ? 0.3 : 0.8
                      ),
                      border: `1px solid ${alpha(
                        isDark ? theme.palette.divider : "#e2e8f0",
                        isDark ? 0.2 : 0.3
                      )}`,
                      boxShadow: theme.shadows[4],
                    }}
                  >
                    <TextField
                      label="Total Amount"
                      type="text"
                      name="totalAmount"
                      value={formik.values.totalAmount}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.totalAmount &&
                        Boolean(formik.errors.totalAmount)
                      }
                      helperText={
                        formik.touched.totalAmount && formik.errors.totalAmount
                      }
                      fullWidth
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          backdropFilter: "blur(10px)",
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.2 : 0.7
                          ),
                          "&:hover": {
                            backgroundColor: alpha(
                              isDark
                                ? theme.palette.background.paper
                                : "#ffffff",
                              isDark ? 0.3 : 0.8
                            ),
                          },
                        },
                        "& .MuiOutlinedInput-input": {
                          color: isDark ? "#e2e8f0" : "#1e293b",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                      }}
                    />
                  </Card>
                )}

                {/* GST + DISCOUNT SECTION */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: "blur(20px)",
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : "#ffffff",
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: isDark ? "#e2e8f0" : "#1e293b",
                        mb: 1,
                      }}
                    >
                      GST Settings
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={showGST}
                          onChange={(e) => {
                            setShowGST(e.target.checked);
                            if (!e.target.checked) {
                              setGstMode("add");
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={
                        <Typography
                          sx={{ color: isDark ? "#e2e8f0" : "#1e293b" }}
                        >
                          Apply GST (3%)
                        </Typography>
                      }
                      sx={{ mb: 1 }}
                    />

                    {showGST && (
                      <>
                        <RadioGroup
                          row
                          value={gstMode}
                          onChange={(e) => setGstMode(e.target.value)}
                          sx={{ ml: 2, mb: 2 }}
                        >
                          <FormControlLabel
                            value="add"
                            control={<Radio size="small" />}
                            label={
                              <Typography
                                variant="body2"
                                sx={{ color: isDark ? "#e2e8f0" : "#1e293b" }}
                              >
                                Add GST 3%
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            value="adjust"
                            control={<Radio size="small" />}
                            label={
                              <Typography
                                variant="body2"
                                sx={{ color: isDark ? "#e2e8f0" : "#1e293b" }}
                              >
                                Adjust Making Charges
                              </Typography>
                            }
                          />
                        </RadioGroup>

                        <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                          <InputLabel
                            sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                          >
                            GST Type
                          </InputLabel>
                          <Select
                            value={gstType}
                            onChange={(e) => setGstType(e.target.value)}
                            label="GST Type"
                            sx={{
                              borderRadius: 2,
                              backdropFilter: "blur(10px)",
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.2 : 0.7
                              ),
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: alpha(
                                  isDark ? theme.palette.divider : "#e2e8f0",
                                  isDark ? 0.2 : 0.3
                                ),
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: alpha(
                                  isDark ? "#60a5fa" : "#3b82f6",
                                  isDark ? 0.4 : 0.5
                                ),
                              },
                              "& .MuiSelect-select": {
                                color: isDark ? "#e2e8f0" : "#1e293b",
                              },
                            }}
                          >
                            <MenuItem value="cgst_sgst">
                              CGST + SGST (1.5% each)
                            </MenuItem>
                            <MenuItem value="igst">IGST (3%)</MenuItem>
                          </Select>
                        </FormControl>

                        <Box
                          sx={{
                            mb: 2,
                            p: 2,
                            borderRadius: 2,
                            background: alpha(
                              isDark ? "#1e293b" : "#f8fafc",
                              isDark ? 0.4 : 0.5
                            ),
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#94a3b8" : "#64748b",
                              display: "block",
                              mb: 1,
                            }}
                          >
                            {gstType === "igst"
                              ? "IGST (Integrated GST) - 3% applicable for inter-state transactions"
                              : "CGST + SGST (Central & State GST) - 1.5% each applicable for intra-state transactions"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDark ? "#60a5fa" : "#3b82f6",
                            }}
                          >
                            {gstType === "igst"
                              ? `IGST Amount: ₹${calculations.igst.toFixed(2)}`
                              : `CGST: ₹${calculations.cgst.toFixed(
                                2
                              )} | SGST: ₹${calculations.sgst.toFixed(2)}`}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Box>

                  <TextField
                    label="Discount"
                    type="text"
                    name="discount"
                    value={formik.values.discount}
                    onChange={formik.handleChange}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        backdropFilter: "blur(10px)",
                        backgroundColor: alpha(
                          isDark ? theme.palette.background.paper : "#ffffff",
                          isDark ? 0.2 : 0.7
                        ),
                        "&:hover": {
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.3 : 0.8
                          ),
                        },
                      },
                      "& .MuiOutlinedInput-input": {
                        color: isDark ? "#e2e8f0" : "#1e293b",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <LocalOfferIcon
                            fontSize="small"
                            sx={{ color: isDark ? "#cbd5e1" : "#64748b" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Card>

                {/* PAYMENT SECTION */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: "blur(20px)",
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : "#ffffff",
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: isDark ? "#e2e8f0" : "#1e293b",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <Payments fontSize="small" />
                    Payment Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cash"
                        type="text"
                        name="cashPayment"
                        value={formik.values.cashPayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backdropFilter: "blur(10px)",
                            backgroundColor: alpha(
                              isDark
                                ? theme.palette.background.paper
                                : "#ffffff",
                              isDark ? 0.2 : 0.7
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.3 : 0.8
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Online"
                        type="text"
                        name="onlinePayment"
                        value={formik.values.onlinePayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backdropFilter: "blur(10px)",
                            backgroundColor: alpha(
                              isDark
                                ? theme.palette.background.paper
                                : "#ffffff",
                              isDark ? 0.2 : 0.7
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.3 : 0.8
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Cheque"
                        type="text"
                        name="chequePayment"
                        value={formik.values.chequePayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backdropFilter: "blur(10px)",
                            backgroundColor: alpha(
                              isDark
                                ? theme.palette.background.paper
                                : "#ffffff",
                              isDark ? 0.2 : 0.7
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.3 : 0.8
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Other Payment"
                        type="text"
                        name="otherPayment"
                        value={formik.values.otherPayment}
                        onChange={formik.handleChange}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backdropFilter: "blur(10px)",
                            backgroundColor: alpha(
                              isDark
                                ? theme.palette.background.paper
                                : "#ffffff",
                              isDark ? 0.2 : 0.7
                            ),
                            "&:hover": {
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.3 : 0.8
                              ),
                            },
                          },
                          "& .MuiOutlinedInput-input": {
                            color: isDark ? "#e2e8f0" : "#1e293b",
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* ADVANCE PAYMENT SWITCH */}
                  <FormControlLabel
                    sx={{ mt: 2 }}
                    control={
                      <Checkbox
                        checked={enableAdvancePayment}
                        onChange={(e) =>
                          setEnableAdvancePayment(e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AccountBalanceIcon fontSize="small" />
                        <Typography
                          sx={{ color: isDark ? "#e2e8f0" : "#1e293b" }}
                        >
                          Advance Payments
                        </Typography>
                      </Stack>
                    }
                  />

                  {/* ADVANCE PAYMENT SECTION */}
                  {enableAdvancePayment && (
                    <Box mt={2}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? "#e2e8f0" : "#1e293b",
                          mb: 1,
                        }}
                      >
                        Select Advance Payment Modes
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {Object.entries(paymentModes).map(([mode, enabled]) => (
                          <Chip
                            key={mode}
                            label={mode.charAt(0).toUpperCase() + mode.slice(1)}
                            clickable
                            onClick={() => handlePaymentModeChange(mode)}
                            color={enabled ? "primary" : "default"}
                            variant={enabled ? "filled" : "outlined"}
                            sx={{
                              mb: 1,
                              backdropFilter: "blur(10px)",
                              backgroundColor: enabled
                                ? alpha(
                                  isDark
                                    ? theme.palette.primary.main
                                    : theme.palette.primary.main,
                                  isDark ? 0.3 : 0.2
                                )
                                : "transparent",
                            }}
                          />
                        ))}
                      </Stack>

                      {/* Custom "Other" Option */}
                      {paymentModes.other && (
                        <Stack direction="row" spacing={1} mt={2}>
                          <TextField
                            label="Custom Payment Type"
                            size="small"
                            value={customOther}
                            onChange={(e) => setCustomOther(e.target.value)}
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 1.5,
                                backdropFilter: "blur(10px)",
                                backgroundColor: alpha(
                                  isDark
                                    ? theme.palette.background.paper
                                    : "#ffffff",
                                  isDark ? 0.2 : 0.7
                                ),
                              },
                            }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleAddCustomOther}
                            sx={{
                              borderRadius: 1.5,
                              backdropFilter: "blur(10px)",
                              backgroundColor: alpha(
                                isDark
                                  ? theme.palette.background.paper
                                  : "#ffffff",
                                isDark ? 0.3 : 0.8
                              ),
                            }}
                          >
                            Add
                          </Button>
                        </Stack>
                      )}

                      {/* Advance Fields */}
                      <Grid container spacing={2} mt={1}>
                        {Object.entries(paymentModes).map(
                          ([mode, enabled]) =>
                            enabled &&
                            mode !== "other" && (
                              <Grid item xs={12} md={6} key={mode}>
                                <TextField
                                  label={`Advance ${mode}`}
                                  type="text"
                                  size="small"
                                  fullWidth
                                  value={advancePayments[mode] || ""}
                                  onChange={(e) =>
                                    handleAdvancePaymentChange(
                                      mode,
                                      e.target.value
                                    )
                                  }
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: 1.5,
                                      backdropFilter: "blur(10px)",
                                      backgroundColor: alpha(
                                        isDark
                                          ? theme.palette.background.paper
                                          : "#ffffff",
                                        isDark ? 0.2 : 0.7
                                      ),
                                    },
                                  }}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        ₹
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            )
                        )}

                        {/* Custom Other Advance Fields */}
                        {otherOptions.map((opt) => (
                          <Grid item xs={12} md={6} key={opt}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <TextField
                                label={`Advance ${opt}`}
                                type="text"
                                size="small"
                                fullWidth
                                value={advancePayments[opt] || ""}
                                onChange={(e) =>
                                  handleCustomOtherPaymentChange(
                                    opt,
                                    e.target.value
                                  )
                                }
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1.5,
                                    backdropFilter: "blur(10px)",
                                    backgroundColor: alpha(
                                      isDark
                                        ? theme.palette.background.paper
                                        : "#ffffff",
                                      isDark ? 0.2 : 0.7
                                    ),
                                  },
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      ₹
                                    </InputAdornment>
                                  ),
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveCustomOther(opt)}
                                sx={{
                                  borderRadius: 1,
                                  backdropFilter: "blur(10px)",
                                  backgroundColor: alpha(
                                    isDark
                                      ? theme.palette.error.main
                                      : theme.palette.error.main,
                                    isDark ? 0.2 : 0.1
                                  ),
                                  color: theme.palette.error.main,
                                  "&:hover": {
                                    backgroundColor: alpha(
                                      theme.palette.error.main,
                                      isDark ? 0.3 : 0.15
                                    ),
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>

                      {/* ADVANCE TOTAL */}
                      <TextField
                        label="Advance Total"
                        fullWidth
                        size="small"
                        value={advanceTotal}
                        sx={{ mt: 2 }}
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">₹</InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}

                  {/* PAYMENT DONE */}
                  <TextField
                    label="Total Payment Done"
                    fullWidth
                    margin="normal"
                    size="small"
                    value={formik.values.paymentDone}
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                  />
                </Card>
              </Stack>
            </Grid>

            {/* RIGHT COLUMN — SUMMARY + ACTION BUTTONS */}
            <Grid item size={{ xs: 12, md: 12 }}>
              <Stack
                spacing={3}
                sx={{
                  position: "sticky",
                  top: 24,
                }}
              >
                {/* SUMMARY PANEL */}
                <Card
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    backdropFilter: "blur(20px)",
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : "#ffffff",
                      isDark ? 0.3 : 0.3
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                    overflow: "hidden",
                    position: "relative",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "4px",
                      background: isDark
                        ? "linear-gradient(90deg, #60A5FA 0%, #3B82F6 50%, #1D4ED8 100%)"
                        : "linear-gradient(90deg, #2563EB 0%, #1D4ED8 50%, #1E40AF 100%)",
                    },
                  }}
                >
                  {/* HEADER */}
                  <Box
                    sx={{
                      p: 3,
                      pb: 2,
                      background: alpha(
                        isDark ? "#1e293b" : "#f8fafc",
                        isDark ? 0.4 : 0.5
                      ),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          background: isDark
                            ? "linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)"
                            : "linear-gradient(135deg, #1E293B 0%, #334155 100%)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: isDark
                              ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                              : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                            color: "white",
                          }}
                        >
                          <Summarize fontSize="small" />
                        </Box>
                        Transaction Summary
                      </Typography>

                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          background: alpha(
                            isDark ? "#10b981" : "#10b981",
                            isDark ? 0.2 : 0.15
                          ),
                          border: `1px solid ${alpha(
                            isDark ? "#10b981" : "#059669",
                            isDark ? 0.3 : 0.2
                          )}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: isDark ? "#34d399" : "#059669",
                            fontSize: "0.7rem",
                          }}
                        >
                          {showGST ? "GST INVOICE" : "CASH MEMO"}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="caption"
                      sx={{
                        color: isDark ? "#94a3b8" : "#64748b",
                        display: "block",
                        pl: 5.5,
                      }}
                    >
                      Real-time calculations • Auto-updating
                    </Typography>
                  </Box>

                  <Box sx={{ p: 3 }}>
                    {/* AMOUNT BREAKDOWN */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Calculate fontSize="small" />
                          Amount Breakdown
                        </Typography>
                      </Box>

                      {/* SUBTOTAL */}
                      <Box sx={{ mb: 2 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDark ? "#cbd5e1" : "#64748b",
                              fontSize: "0.8rem",
                            }}
                          >
                            Subtotal
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: isDark ? "#e2e8f0" : "#1e293b",
                              fontSize: "0.9rem",
                            }}
                          >
                            ₹{calculations.subtotal.toFixed(2)}
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            height: "4px",
                            borderRadius: "2px",
                            background: alpha(
                              isDark ? "#60a5fa" : "#3b82f6",
                              isDark ? 0.3 : 0.2
                            ),
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: "100%",
                              background: isDark
                                ? "linear-gradient(90deg, #60A5FA 0%, #3B82F6 100%)"
                                : "linear-gradient(90deg, #2563EB 0%, #1D4ED8 100%)",
                              borderRadius: "2px",
                            }}
                          />
                        </Box>
                      </Box>

                      {/* GST BREAKDOWN */}
                      {showGST && (
                        <>
                          {gstType === "igst" ? (
                            <Box sx={{ mb: 2 }}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 1 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isDark ? "#cbd5e1" : "#64748b",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  IGST (3%)
                                </Typography>
                                <Typography
                                  sx={{
                                    color: isDark ? "#e2e8f0" : "#1e293b",
                                    fontSize: "0.9rem",
                                  }}
                                >
                                  ₹{calculations.igst.toFixed(2)}
                                </Typography>
                              </Stack>
                              <Box
                                sx={{
                                  height: "3px",
                                  borderRadius: "1.5px",
                                  background: alpha(
                                    isDark ? "#8b5cf6" : "#8b5cf6",
                                    isDark ? 0.3 : 0.2
                                  ),
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    height: "100%",
                                    width: "100%",
                                    background: isDark
                                      ? "linear-gradient(90deg, #A78BFA 0%, #8B5CF6 100%)"
                                      : "linear-gradient(90deg, #8B5CF6 0%, #7C3AED 100%)",
                                    borderRadius: "1.5px",
                                  }}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <>
                              <Box sx={{ mb: 2 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ mb: 1 }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#cbd5e1" : "#64748b",
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    CGST (1.5%)
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: isDark ? "#e2e8f0" : "#1e293b",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    ₹{calculations.cgst.toFixed(2)}
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    height: "3px",
                                    borderRadius: "1.5px",
                                    background: alpha(
                                      isDark ? "#34d399" : "#10b981",
                                      isDark ? 0.3 : 0.2
                                    ),
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: "100%",
                                      width: "100%",
                                      background: isDark
                                        ? "linear-gradient(90deg, #34D399 0%, #10B981 100%)"
                                        : "linear-gradient(90deg, #10B981 0%, #059669 100%)",
                                      borderRadius: "1.5px",
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box sx={{ mb: 2 }}>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ mb: 1 }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: isDark ? "#cbd5e1" : "#64748b",
                                      fontSize: "0.8rem",
                                    }}
                                  >
                                    SGST (1.5%)
                                  </Typography>
                                  <Typography
                                    sx={{
                                      color: isDark ? "#e2e8f0" : "#1e293b",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    ₹{calculations.sgst.toFixed(2)}
                                  </Typography>
                                </Stack>
                                <Box
                                  sx={{
                                    height: "3px",
                                    borderRadius: "1.5px",
                                    background: alpha(
                                      isDark ? "#34d399" : "#10b981",
                                      isDark ? 0.3 : 0.2
                                    ),
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      height: "100%",
                                      width: "100%",
                                      background: isDark
                                        ? "linear-gradient(90deg, #34D399 0%, #10B981 100%)"
                                        : "linear-gradient(90deg, #10B981 0%, #059669 100%)",
                                      borderRadius: "1.5px",
                                    }}
                                  />
                                </Box>
                              </Box>
                            </>
                          )}
                        </>
                      )}

                      {/* DISCOUNT */}
                      {formik.values.discount > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mb: 1 }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#cbd5e1" : "#64748b",
                                fontSize: "0.8rem",
                              }}
                            >
                              Discount
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "error.main",
                                fontSize: "0.9rem",
                              }}
                            >
                              -₹{parseFloat(formik.values.discount).toFixed(2)}
                            </Typography>
                          </Stack>
                          <Box
                            sx={{
                              height: "3px",
                              borderRadius: "1.5px",
                              background: alpha(
                                isDark
                                  ? theme.palette.error.main
                                  : theme.palette.error.main,
                                isDark ? 0.3 : 0.2
                              ),
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                height: "100%",
                                width: "100%",
                                background: isDark
                                  ? "linear-gradient(90deg, #F87171 0%, #EF4444 100%)"
                                  : "linear-gradient(90deg, #EF4444 0%, #DC2626 100%)",
                                borderRadius: "1.5px",
                              }}
                            />
                          </Box>
                        </Box>
                      )}

                      {/* GRAND TOTAL */}
                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 1,
                          background: isDark
                            ? "linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(29, 78, 216, 0.1) 100%)"
                            : "linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(29, 78, 216, 0.08) 100%)",
                          border: `1px solid ${alpha(
                            isDark ? "#3b82f6" : "#2563eb",
                            isDark ? 0.2 : 0.15
                          )}`,
                          mt: 2,
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: isDark ? "#e2e8f0" : "#1e293b",
                            }}
                          >
                            Grand Total
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: "1.25rem",
                              background: isDark
                                ? "linear-gradient(135deg, #93C5FD 0%, #60A5FA 100%)"
                                : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                            }}
                          >
                            ₹{calculations.grandTotal.toFixed(2)}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>

                    {/* PAYMENT SUMMARY */}
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Payments fontSize="small" />
                          Payment Summary
                        </Typography>
                      </Box>

                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? "#10b981" : "#10b981",
                                isDark ? 0.15 : 0.1
                              ),
                              border: `1px solid ${alpha(
                                isDark ? "#10b981" : "#059669",
                                isDark ? 0.2 : 0.15
                              )}`,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              Paid
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: isDark ? "#34d399" : "#059669",
                                fontSize: "1rem",
                              }}
                            >
                              ₹{formik.values.paymentDone.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={6}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background:
                                pendingAmount > 0
                                  ? alpha(
                                    isDark
                                      ? theme.palette.error.main
                                      : theme.palette.error.main,
                                    isDark ? 0.15 : 0.1
                                  )
                                  : alpha(
                                    isDark ? "#10b981" : "#10b981",
                                    isDark ? 0.15 : 0.1
                                  ),
                              border: `1px solid ${pendingAmount > 0
                                ? alpha(
                                  isDark
                                    ? theme.palette.error.main
                                    : theme.palette.error.main,
                                  isDark ? 0.2 : 0.15
                                )
                                : alpha(
                                  isDark ? "#10b981" : "#059669",
                                  isDark ? 0.2 : 0.15
                                )
                                }`,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              {pendingAmount > 0 ? "Pending" : "Balance"}
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color:
                                  pendingAmount > 0
                                    ? isDark
                                      ? "#f87171"
                                      : "#dc2626"
                                    : isDark
                                      ? "#34d399"
                                      : "#059669",
                                fontSize: "1rem",
                              }}
                            >
                              ₹{pendingAmount.toFixed(2)}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    {/* QUICK STATS */}
                    <Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#e2e8f0" : "#1e293b",
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <InfoIcon fontSize="small" />
                          Quick Stats
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? "#1e293b" : "#f8fafc",
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : "#e2e8f0",
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              Items
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: isDark ? "#60a5fa" : "#3b82f6",
                                fontSize: "1.2rem",
                              }}
                            >
                              {items.length}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? "#1e293b" : "#f8fafc",
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : "#e2e8f0",
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              GST
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color: showGST
                                  ? isDark
                                    ? "#34d399"
                                    : "#059669"
                                  : isDark
                                    ? "#94a3b8"
                                    : "#64748b",
                                fontSize: "1.2rem",
                              }}
                            >
                              {showGST
                                ? gstType === "igst"
                                  ? "IGST"
                                  : "CGST/SGST"
                                : "OFF"}
                            </Typography>
                          </Box>
                        </Grid>

                        <Grid item xs={4}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              background: alpha(
                                isDark ? "#1e293b" : "#f8fafc",
                                isDark ? 0.4 : 0.6
                              ),
                              border: `1px solid ${alpha(
                                isDark ? theme.palette.divider : "#e2e8f0",
                                isDark ? 0.2 : 0.3
                              )}`,
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: isDark ? "#94a3b8" : "#64748b",
                                display: "block",
                                mb: 0.5,
                              }}
                            >
                              Mode
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 800,
                                color:
                                  showGST && gstMode === "adjust"
                                    ? isDark
                                      ? "#f59e0b"
                                      : "#d97706"
                                    : isDark
                                      ? "#94a3b8"
                                      : "#64748b",
                                fontSize: "1.2rem",
                              }}
                            >
                              {showGST
                                ? gstMode === "add"
                                  ? "ADD"
                                  : "ADJ"
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>
                </Card>

                {/* Pending Adjustments Summary */}
                {Object.keys(weightAdjustments).length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <ScaleIcon fontSize="small" />
                        Pending Weight Adjustments
                      </Typography>
                    </Box>

                    <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                      {Object.keys(weightAdjustments).length} item(s) have weight adjustments pending.
                      These adjustments will be applied when the transaction is saved.
                    </Alert>

                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Original Weight</TableCell>
                            <TableCell>New Weight</TableCell>
                            <TableCell>Adjustment</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.values(weightAdjustments).map(({ item, adjustment }) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.name}</TableCell>
                              <TableCell>{item.weightAdjustmentData?.originalWeight}g</TableCell>
                              <TableCell>{item.weight}g</TableCell>
                              <TableCell>
                                <Chip
                                  label={adjustment.action === 'ignore' ? 'Ignored' :
                                    adjustment.action === 'gross_stock' ? `Gross Stock (${adjustment.metal})` :
                                      adjustment.action === 'another_item' ? `Another Item (${adjustment.item?.name})` :
                                        'New Item Created'}
                                  size="small"
                                  color={adjustment.action === 'ignore' ? 'default' : 'success'}
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => handleManualWeightAdjustment(item)}
                                  sx={{
                                    borderRadius: 1,
                                    backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                    color: theme.palette.warning.main,
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* ACTION BUTTONS */}
                <Card
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    backdropFilter: "blur(20px)",
                    backgroundColor: alpha(
                      isDark ? theme.palette.background.paper : "#ffffff",
                      isDark ? 0.3 : 0.6
                    ),
                    border: `1px solid ${alpha(
                      isDark ? theme.palette.divider : "#e2e8f0",
                      isDark ? 0.2 : 0.3
                    )}`,
                    boxShadow: theme.shadows[4],
                  }}
                >
                  <form onSubmit={formik.handleSubmit}>
                    <Stack direction="row" spacing={2}>
                      {!isTransactionSaved && (
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          size="large"
                          fullWidth
                          sx={{
                            py: 1.4,
                            fontWeight: 700,
                            borderRadius: 2,
                            background: isDark
                              ? "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)"
                              : "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                            "&:hover": {
                              background: isDark
                                ? "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
                                : "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
                            },
                          }}
                        >
                          Save Transaction
                        </Button>
                      )}

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        size="large"
                        fullWidth
                        // disabled={
                        //   items.length === 0 && transactionType === "detailed"
                        // }
                        disabled={true}
                        onClick={handlePrintBill}
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 2,
                          backdropFilter: "blur(10px)",
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1.5px solid ${alpha(
                            isDark ? "#8b5cf6" : "#8b5cf6",
                            isDark ? 0.4 : 0.5
                          )}`,
                          color: isDark ? "#a78bfa" : "#8b5cf6",
                        }}
                      >
                        Print Bill
                      </Button>

                      <Button
                        type="button"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        size="large"
                        fullWidth
                        onClick={resetForm}
                        sx={{
                          py: 1.4,
                          fontWeight: 600,
                          borderRadius: 2,
                          backdropFilter: "blur(10px)",
                          backgroundColor: alpha(
                            isDark ? theme.palette.background.paper : "#ffffff",
                            isDark ? 0.3 : 0.8
                          ),
                          border: `1.5px solid ${alpha(
                            isDark ? theme.palette.divider : "#e2e8f0",
                            isDark ? 0.3 : 0.4
                          )}`,
                          color: isDark ? "#e2e8f0" : "#475569",
                        }}
                      >
                        Reset Form
                      </Button>
                    </Stack>
                  </form>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <WeightAdjustmentDialog
          open={adjustmentDialogOpen}
          onClose={() => {
            setAdjustmentDialogOpen(false);
            setCurrentAdjustmentItem(null);
          }}
          item={currentAdjustmentItem}
          originalWeight={currentAdjustmentItem?.originalWeight || currentAdjustmentItem?.weight || 0}
          newWeight={currentAdjustmentItem?.newWeight || currentAdjustmentItem?.weight || 0}
          weightDifference={Math.abs((currentAdjustmentItem?.newWeight || currentAdjustmentItem?.weight || 0) - (currentAdjustmentItem?.originalWeight || currentAdjustmentItem?.weight || 0))}
          isWeightIncreased={(currentAdjustmentItem?.newWeight || 0) > (currentAdjustmentItem?.originalWeight || 0)}
          onAdjustmentComplete={handleAdjustmentComplete}
          onSkip={handleSkipAdjustment}
        />

        <WeightEditDialog
          open={weightEditDialogOpen}
          onClose={() => {
            // setWeightEditDialogOpen(false);
            setEditingItemWeight(null);
          }}
          item={editingItemWeight}
          onConfirm={handleWeightChangeConfirm}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default CustomerTransaction;
